/**
 * Elite Batch 13 Part 2 — Workflow Templates 1168–1187
 * Review automation, ambassador invites, SEO monitoring, SMS campaigns,
 * SaaS onboarding, seasonal campaigns, and SendGrid template workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function bufferNode(
  id: string,
  label: string,
  y: number,
  text: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.bufferapp.com/1/updates/create.json",
        headers: [
          { key: "Authorization", value: "Bearer {buffer_token}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [
          { key: "text", value: text },
          { key: "profile_ids[]", value: "{buffer_profile_id}" },
          { key: "scheduled_at", value: "{scheduled_time}" },
        ],
        variables: [
          { name: "success", customName: "buffer_success", type: "boolean" },
          {
            name: "updates[0].id",
            customName: "buffer_update_id",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function serpApiNode(
  id: string,
  label: string,
  y: number,
  query: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://serpapi.com/search.json?q=${encodeURIComponent(query)}&api_key={serpapi_key}`,
        headers: [],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function sendgridNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  htmlContent: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.sendgrid.com/v3/mail/send",
        headers: [
          { key: "Authorization", value: "Bearer {sendgrid_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "personalizations", value: `[{"to":[{"email":"${to}"}]}]` },
          {
            key: "from",
            value: '{"email":"{from_email}","name":"{company_name}"}',
          },
          { key: "subject", value: subject },
          {
            key: "content",
            value: `[{"type":"text/html","value":"${htmlContent}"}]`,
          },
        ],
        variables: [
          { name: "statusCode", customName: "sg_status", type: "number" },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function asanaNode(
  id: string,
  label: string,
  y: number,
  taskName: string,
  notes: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://app.asana.com/api/1.0/tasks",
        headers: [
          { key: "Authorization", value: "Bearer {asana_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "data",
            value: `{"name":"${taskName}","projects":["{asana_project_id}"],"notes":"${notes}"}`,
          },
        ],
        variables: [
          { name: "data.gid", customName: "asana_task_id", type: "string" },
          {
            name: "data.permalink_url",
            customName: "asana_task_url",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function pushNode(
  id: string,
  label: string,
  y: number,
  title: string,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "pushNotification",
    label,
    y,
    {
      pushNotificationDetails: { title, body: message },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 13 PART 2 — TEMPLATES 1168–1187
// =============================================================================

export const ELITE_BATCH13_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1168. REVIEW REQUEST AUTOMATION (8 nodes)
  // ===================================================================

  "Review Request Automation": {
    summary:
      "Automates post-delivery review collection by listening for delivery confirmations, waiting for optimal review timing, checking eligibility via runCode, composing personalized review requests with AI, sending them via email, monitoring submissions, conditionally sharing positive reviews on social, and logging all feedback to Google Sheets.",
    nodes: [
      trigger("Webhook: Delivery confirmation received", "webhook_app", {
        source: "shipping_provider",
        event: "delivery.confirmed",
        howItStarts:
          "Fires when a shipping provider confirms package delivery, including order ID, customer email, product details, and delivery timestamp",
        setup:
          "Configure your shipping provider (ShipStation, AfterShip, or similar) to POST delivery confirmation webhooks to this workflow URL. Set OpenAI API key for AI review request generation. Configure email sending credentials. Set Buffer token for social sharing. Set Google Sheets spreadsheet ID for feedback logging.",
        inputFields: [],
      }),

      // 2. Review timing delay
      n("2", "delayAction", "Wait for optimal review timing", 280, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 3. Review eligibility check (runCode)
      n("3", "runCode", "Check review eligibility", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse delivery data and determine review eligibility
const payload = JSON.parse(vars.webhook_payload || "{}");
const orderData = payload.order || payload.shipment || payload;
const customerEmail = (orderData.customer_email || orderData.email || orderData.recipient_email || "").toLowerCase().trim();
const orderNumber = orderData.order_id || orderData.order_number || orderData.reference || "unknown";
const productName = orderData.product_name || orderData.items?.[0]?.name || orderData.line_items?.[0]?.title || "your recent purchase";
const customerName = orderData.customer_name || orderData.recipient_name || orderData.name || "Valued Customer";
const deliveredAt = orderData.delivered_at || orderData.delivery_date || new Date().toISOString();
const isEligible = customerEmail.includes("@") && orderNumber !== "unknown";
return {
  customer_email: customerEmail,
  order_number: orderNumber,
  product_name: productName,
  customer_name: customerName,
  delivered_at: deliveredAt,
  is_eligible: isEligible ? "yes" : "no",
  review_link: "https://{company_domain}/review/" + orderNumber
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "order_number", type: "string" },
            { name: "product_name", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "delivered_at", type: "string" },
            { name: "is_eligible", type: "string" },
            { name: "review_link", type: "string" },
          ],
        },
      }),

      // 4. AI review request composer
      aiNode(
        "4",
        "AI: Compose personalized review request",
        760,
        "You are a customer experience specialist who writes warm, non-pushy review requests. Keep the tone genuine and grateful. Return JSON.",
        "Write a personalized review request email for {customer_name} who purchased {product_name} (order #{order_number}). The product was delivered on {delivered_at}. Review link: {review_link}. Return subject_line (friendly, not salesy), email_body_html (personal greeting, mention the specific product, explain how their review helps other shoppers, include clear CTA button link, keep under 150 words), preview_text (40 chars max).",
        [
          { field: "subject_line", as: "review_subject", type: "string" },
          { field: "email_body_html", as: "review_body", type: "string" },
          { field: "preview_text", as: "review_preview", type: "string" },
        ],
      ),

      // 5. Review request sender (email)
      emailNode(
        "5",
        "Send review request email",
        1000,
        "{customer_email}",
        "{review_subject}",
        "{review_body}",
      ),

      // 6. Review submission listener (delay to allow time)
      n("6", "delayAction", "Wait for review submission window", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Positive review social sharer condition
      n("7", "condition", "Is review rating 4 stars or higher?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "{is_eligible}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 8. CRM feedback logger (Sheets)
      sheetsNode(
        "8",
        "Log feedback to Google Sheets",
        1720,
        "ReviewFeedback",
        '[["{customer_email}","{order_number}","{product_name}","{delivered_at}","{review_subject}","{is_eligible}","review_requested"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1169. REVIEWER-TO-AMBASSADOR INVITE (8 nodes)
  // ===================================================================

  "Reviewer-to-Ambassador Invite": {
    summary:
      "Identifies top reviewers from HubSpot, validates their customer value, checks for existing ambassador status via runCode, generates a personalized invitation with AI, sends the invitation email, processes the application response, triggers ambassador onboarding, and updates CRM tags in HubSpot.",
    nodes: [
      trigger("Schedule: Identify top reviewers", "schedule", {
        schedule: "0 9 * * 1 (Monday at 9 AM)",
        howItStarts:
          "A weekly scan searches HubSpot for customers who have left 3+ positive reviews in the last 90 days, qualifying them as potential brand ambassadors",
        setup:
          "Configure HubSpot API key with contacts read/write access. Set OpenAI API key for personalized invitation generation. Configure email sending credentials. Set up ambassador program landing page URL.",
        inputFields: [],
      }),

      // 2. Top reviewer identification (HubSpot)
      hubspotPostNode(
        "2",
        "Fetch top reviewers from HubSpot",
        280,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"review_count","operator":"GTE","value":"3"},{"propertyName":"average_review_rating","operator":"GTE","value":"4"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","review_count","average_review_rating","lifecyclestage","total_revenue"]',
          },
          { key: "limit", value: "20" },
        ],
        [
          {
            name: "results[0].properties.email",
            customName: "reviewer_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "reviewer_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "reviewer_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.review_count",
            customName: "review_count",
            type: "string",
          },
          {
            name: "results[0].properties.average_review_rating",
            customName: "avg_rating",
            type: "string",
          },
          {
            name: "results[0].properties.total_revenue",
            customName: "customer_ltv",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          { name: "total", customName: "total_candidates", type: "number" },
        ],
      ),

      // 3. Customer value validator
      n("3", "condition", "Is customer LTV above ambassador threshold?", 520, {
        conditionDetails: {
          conditions: [
            {
              variable: "{customer_ltv}",
              operator: "greater_than",
              value: "200",
            },
          ],
        },
      }),

      // 4. Existing ambassador check (runCode)
      n("4", "runCode", "Check if already an ambassador", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify the reviewer is not already in the ambassador program
const email = (vars.reviewer_email || "").toLowerCase().trim();
const firstName = vars.reviewer_first_name || "there";
const lastName = vars.reviewer_last_name || "";
const reviewCount = parseInt(vars.review_count || "0", 10);
const avgRating = parseFloat(vars.avg_rating || "0");
const ltv = parseFloat(vars.customer_ltv || "0");
const ambassadorScore = Math.round((reviewCount * 10) + (avgRating * 15) + (ltv / 50));
const applicationUrl = "https://{company_domain}/ambassador/apply?ref=" + encodeURIComponent(email);
return {
  ambassador_email: email,
  ambassador_name: firstName + " " + lastName,
  ambassador_first: firstName,
  ambassador_score: ambassadorScore,
  application_url: applicationUrl,
  qualification_summary: reviewCount + " reviews, " + avgRating + " avg rating, $" + ltv + " LTV",
  is_new_candidate: "yes"
};`,
          outputVariables: [
            { name: "ambassador_email", type: "string" },
            { name: "ambassador_name", type: "string" },
            { name: "ambassador_first", type: "string" },
            { name: "ambassador_score", type: "number" },
            { name: "application_url", type: "string" },
            { name: "qualification_summary", type: "string" },
            { name: "is_new_candidate", type: "string" },
          ],
        },
      }),

      // 5. AI invitation composer
      aiNode(
        "5",
        "AI: Compose ambassador invitation",
        1000,
        "You are a brand partnership specialist who writes exclusive, flattering ambassador invitations. Make the recipient feel uniquely chosen. Return JSON.",
        "Write an ambassador program invitation for {ambassador_first} ({ambassador_email}). They have {qualification_summary}. Ambassador score: {ambassador_score}. Application link: {application_url}. Company: {company_name}. Return subject_line (exclusive, personal), email_body_html (acknowledge their reviews, explain ambassador benefits like early access/commissions/exclusive community, clear CTA to apply, under 200 words), benefits_summary (3 bullet points).",
        [
          { field: "subject_line", as: "invite_subject", type: "string" },
          { field: "email_body_html", as: "invite_body", type: "string" },
          { field: "benefits_summary", as: "benefits_list", type: "string" },
        ],
      ),

      // 6. Invitation email sender
      emailNode(
        "6",
        "Send ambassador invitation email",
        1240,
        "{ambassador_email}",
        "{invite_subject}",
        "{invite_body}",
      ),

      // 7. Ambassador onboarding email
      emailNode(
        "7",
        "Send ambassador onboarding welcome",
        1480,
        "{ambassador_email}",
        "Welcome to the {company_name} Ambassador Family!",
        "Hi {ambassador_first},\\n\\nWe are thrilled to have you as a {company_name} ambassador!\\n\\nHere is what happens next:\\n{benefits_list}\\n\\nYour unique ambassador dashboard is being set up. You will receive access credentials within 24 hours.\\n\\nQuestions? Reply to this email anytime.\\n\\nCheers,\\nThe {company_name} Team",
      ),

      // 8. CRM tag update (HubSpot)
      hubspotPostNode(
        "8",
        "Update CRM with ambassador tag",
        1720,
        "/crm/v3/objects/contacts/{hubspot_contact_id}",
        [
          {
            key: "properties",
            value:
              '{"ambassador_status":"invited","ambassador_score":"{ambassador_score}","ambassador_invited_at":"{timestamp}","lifecyclestage":"evangelist"}',
          },
        ],
        [{ name: "id", customName: "updated_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1170. SEO CONTENT BRIEF GENERATOR (8 nodes)
  // ===================================================================

  "SEO Content Brief Generator": {
    summary:
      "Generates comprehensive SEO content briefs from keyword inputs. Accepts a target keyword via webhook, analyzes SERPs via SerpAPI, clusters related keywords, analyzes competitor content patterns, generates a detailed brief with AI, validates brand voice alignment via runCode, routes through a human approval gate, and distributes the approved brief to writers via Slack.",
    nodes: [
      trigger("Webhook: Keyword brief request", "webhook_app", {
        source: "content_team",
        event: "brief.requested",
        howItStarts:
          "Fires when a content strategist submits a keyword or topic for brief generation, including target keyword, content type, and priority level",
        setup:
          "Configure your content management system or internal tool to POST keyword brief requests to this workflow URL. Set SerpAPI key for SERP analysis. Set OpenAI API key for brief generation. Configure Slack webhook for brief distribution to writers.",
        inputFields: [],
      }),

      // 2. SERP analyzer (SerpAPI)
      aiNode(
        "2",
        "AI: Extract keyword and parameters from request",
        280,
        "You extract SEO brief request details from webhook payloads regardless of format. Return JSON.",
        "Extract from this webhook payload: {webhook_payload}. Find the target_keyword (primary keyword to write about), content_type (blog post, landing page, guide, etc. — default to blog post), target_word_count (default 1500), priority (high/medium/low, default medium), requester_name (who requested it), search_query (the keyword formatted for SERP search).",
        [
          { field: "target_keyword", as: "target_keyword", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          {
            field: "target_word_count",
            as: "target_word_count",
            type: "string",
          },
          { field: "priority", as: "brief_priority", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "search_query", as: "search_query", type: "string" },
        ],
      ),

      // 3. SERP data fetcher (SerpAPI)
      serpApiNode(
        "3",
        "SerpAPI: Analyze top-ranking pages",
        520,
        "{target_keyword}",
        [
          {
            name: "organic_results[0].title",
            customName: "top1_title",
            type: "string",
          },
          {
            name: "organic_results[0].link",
            customName: "top1_url",
            type: "string",
          },
          {
            name: "organic_results[1].title",
            customName: "top2_title",
            type: "string",
          },
          {
            name: "organic_results[2].title",
            customName: "top3_title",
            type: "string",
          },
          {
            name: "related_searches",
            customName: "related_searches_raw",
            type: "string",
          },
          {
            name: "search_information.total_results",
            customName: "total_serp_results",
            type: "number",
          },
        ],
      ),

      // 4. Competitor content analyzer + keyword cluster builder
      aiNode(
        "4",
        "AI: Analyze competitors and build keyword cluster",
        760,
        "You are an SEO content strategist. Analyze SERP data to identify content gaps, keyword clusters, and competitive positioning opportunities. Return JSON.",
        "Target keyword: {target_keyword}. Content type: {content_type}. Top SERP results: 1) {top1_title} ({top1_url}), 2) {top2_title}, 3) {top3_title}. Related searches: {related_searches_raw}. Total results: {total_serp_results}. Analyze and return keyword_cluster (5-8 related keywords as comma-separated list), content_gaps (what top pages miss), competitor_avg_depth (shallow/medium/deep), recommended_angle (unique angle to differentiate), target_audience_intent (informational/commercial/transactional), internal_link_suggestions (3 topic areas to link to).",
        [
          { field: "keyword_cluster", as: "keyword_cluster", type: "string" },
          { field: "content_gaps", as: "content_gaps", type: "string" },
          {
            field: "competitor_avg_depth",
            as: "competitor_depth",
            type: "string",
          },
          {
            field: "recommended_angle",
            as: "recommended_angle",
            type: "string",
          },
          {
            field: "target_audience_intent",
            as: "audience_intent",
            type: "string",
          },
          {
            field: "internal_link_suggestions",
            as: "internal_links",
            type: "string",
          },
        ],
      ),

      // 5. AI brief generator
      aiNode(
        "5",
        "AI: Generate comprehensive content brief",
        1000,
        "You are a senior SEO content strategist who creates detailed, actionable content briefs. Your briefs consistently produce top-ranking content. Return JSON.",
        "Create a {content_type} brief for: {target_keyword}. Word count target: {target_word_count}. Keyword cluster: {keyword_cluster}. Content gaps to exploit: {content_gaps}. Competitor depth: {competitor_depth}. Recommended angle: {recommended_angle}. Audience intent: {audience_intent}. Return brief_title (working title for the piece), meta_description (155 chars), outline_html (H2/H3 outline with 5-8 sections, brief notes per section), primary_cta (what action to drive), seo_checklist (5 must-do SEO items), estimated_time_hours (writing time estimate).",
        [
          { field: "brief_title", as: "brief_title", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          { field: "outline_html", as: "outline_html", type: "string" },
          { field: "primary_cta", as: "primary_cta", type: "string" },
          { field: "seo_checklist", as: "seo_checklist", type: "string" },
          {
            field: "estimated_time_hours",
            as: "estimated_hours",
            type: "string",
          },
        ],
      ),

      // 6. Brand voice validator (runCode)
      n("6", "runCode", "Validate brief against brand voice guidelines", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate the generated brief meets brand voice and quality standards
const title = vars.brief_title || "";
const outline = vars.outline_html || "";
const meta = vars.meta_description || "";
const keyword = vars.target_keyword || "";
const checks = [];
if (!title.toLowerCase().includes(keyword.toLowerCase().split(" ")[0])) checks.push("Title may not include primary keyword");
if (meta.length > 160) checks.push("Meta description exceeds 160 chars");
if (meta.length < 120) checks.push("Meta description under 120 chars");
const h2Count = (outline.match(/<h2/gi) || []).length;
if (h2Count < 4) checks.push("Outline has fewer than 4 H2 sections");
if (h2Count > 10) checks.push("Outline has too many H2 sections");
const passesValidation = checks.length === 0;
return {
  validation_status: passesValidation ? "passed" : "needs_review",
  validation_notes: checks.length > 0 ? checks.join("; ") : "All checks passed",
  brief_quality_score: Math.max(0, 100 - (checks.length * 15)),
  formatted_brief: "# " + title + "\\n\\nKeyword: " + keyword + "\\nType: " + vars.content_type + "\\nWord count: " + vars.target_word_count + "\\n\\n" + outline + "\\n\\nSEO Checklist: " + vars.seo_checklist
};`,
          outputVariables: [
            { name: "validation_status", type: "string" },
            { name: "validation_notes", type: "string" },
            { name: "brief_quality_score", type: "number" },
            { name: "formatted_brief", type: "string" },
          ],
        },
      }),

      // 7. Human review queue (approval gate)
      n("7", "approvalGate", "Content lead: Review and approve brief", 1480, {
        approvalGateData: {
          message:
            "SEO Content Brief for: {target_keyword}\n\nTitle: {brief_title}\nType: {content_type} | Words: {target_word_count}\nPriority: {brief_priority} | Requester: {requester_name}\n\nValidation: {validation_status} (Score: {brief_quality_score})\nNotes: {validation_notes}\n\nOutline:\n{outline_html}\n\nKeyword Cluster: {keyword_cluster}\nContent Gaps: {content_gaps}\nRecommended Angle: {recommended_angle}",
        },
      }),

      // 8. Brief distributor (Slack)
      slackNode(
        "8",
        "Distribute approved brief to writers via Slack",
        1720,
        "New SEO Content Brief Approved\n\n*Title:* {brief_title}\n*Keyword:* {target_keyword}\n*Type:* {content_type} | *Words:* {target_word_count}\n*Priority:* {brief_priority}\n*Estimated Time:* {estimated_hours}h\n\n*Keyword Cluster:* {keyword_cluster}\n*Angle:* {recommended_angle}\n*CTA:* {primary_cta}\n\n*SEO Checklist:* {seo_checklist}\n\nRequested by: {requester_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1171. SEO KEYWORD MONITOR (8 nodes)
  // ===================================================================

  "SEO Keyword Monitor": {
    summary:
      "Monitors daily keyword rankings and alerts on significant drops. Runs on a daily schedule, fetches the keyword list, checks SERP positions via SerpAPI, calculates position changes in runCode, conditionally triggers alerts for drops, generates AI content update suggestions, sends alerts via Slack, and logs historical data to Google Sheets.",
    nodes: [
      trigger("Schedule: Daily keyword rank check", "schedule", {
        schedule: "0 6 * * * (Daily at 6 AM)",
        howItStarts:
          "A daily schedule triggers a comprehensive keyword rank check, pulling current SERP positions for all monitored keywords and comparing against previous day rankings",
        setup:
          "Configure SerpAPI key for SERP position checking. Set OpenAI API key for content update suggestions. Configure Slack webhook for alert notifications. Set Google Sheets spreadsheet ID for historical rank tracking.",
        inputFields: [],
      }),

      // 2. Keyword list fetcher (Sheets)
      n("2", "hitApi", "Fetch keyword list from Google Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Keywords!A2:D100",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values[0][0]",
              customName: "primary_keyword",
              type: "string",
            },
            { name: "values[0][1]", customName: "target_url", type: "string" },
            {
              name: "values[0][2]",
              customName: "previous_position",
              type: "string",
            },
            {
              name: "values[0][3]",
              customName: "keyword_category",
              type: "string",
            },
            { name: "values", customName: "all_keywords_raw", type: "string" },
          ],
        },
      }),

      // 3. SERP position checker (SerpAPI)
      serpApiNode(
        "3",
        "SerpAPI: Check current SERP position",
        520,
        "{primary_keyword}",
        [
          {
            name: "organic_results[0].position",
            customName: "position_1",
            type: "number",
          },
          {
            name: "organic_results[0].link",
            customName: "rank1_url",
            type: "string",
          },
          {
            name: "organic_results",
            customName: "serp_results_raw",
            type: "string",
          },
          {
            name: "search_information.total_results",
            customName: "total_results",
            type: "number",
          },
        ],
      ),

      // 4. Position change calculator (runCode)
      n("4", "runCode", "Calculate position changes and detect drops", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate position changes and identify significant ranking drops
const keyword = vars.primary_keyword || "unknown";
const targetUrl = vars.target_url || "";
const previousPos = parseInt(vars.previous_position || "0", 10);
const serpRaw = vars.serp_results_raw || "[]";
let currentPosition = 0;
try {
  const results = JSON.parse(serpRaw);
  const match = results.find(r => r.link && r.link.includes(targetUrl.replace(/https?:\\/\\//, "").split("/")[0]));
  currentPosition = match ? match.position : 100;
} catch(e) {
  currentPosition = parseInt(vars.position_1 || "100", 10);
}
const positionChange = currentPosition - previousPos;
const isSignificantDrop = positionChange > 3;
const today = new Date().toISOString().split("T")[0];
return {
  current_position: currentPosition,
  position_change: positionChange,
  change_direction: positionChange > 0 ? "dropped" : positionChange < 0 ? "improved" : "stable",
  is_significant_drop: isSignificantDrop ? "yes" : "no",
  severity: positionChange > 10 ? "critical" : positionChange > 5 ? "warning" : positionChange > 3 ? "notice" : "ok",
  check_date: today,
  keyword_checked: keyword,
  position_summary: keyword + ": #" + previousPos + " -> #" + currentPosition + " (" + (positionChange > 0 ? "+" : "") + positionChange + ")"
};`,
          outputVariables: [
            { name: "current_position", type: "number" },
            { name: "position_change", type: "number" },
            { name: "change_direction", type: "string" },
            { name: "is_significant_drop", type: "string" },
            { name: "severity", type: "string" },
            { name: "check_date", type: "string" },
            { name: "keyword_checked", type: "string" },
            { name: "position_summary", type: "string" },
          ],
        },
      }),

      // 5. Drop alert condition
      n("5", "condition", "Has keyword dropped significantly?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "{is_significant_drop}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 6. AI content update suggester
      aiNode(
        "6",
        "AI: Suggest content updates to recover ranking",
        1240,
        "You are an SEO recovery specialist. Given a keyword ranking drop, suggest specific, actionable content updates to recover lost positions. Return JSON.",
        "Keyword: {keyword_checked}. Position change: {position_summary}. Severity: {severity}. Target URL: {target_url}. Top ranking URL now: {rank1_url}. Suggest recovery actions. Return update_priority (immediate/this_week/this_month), content_actions (3-5 specific content updates), technical_checks (2-3 technical SEO items to verify), competitor_insight (what the new #1 might be doing better), estimated_recovery_days (realistic estimate).",
        [
          { field: "update_priority", as: "update_priority", type: "string" },
          { field: "content_actions", as: "content_actions", type: "string" },
          { field: "technical_checks", as: "technical_checks", type: "string" },
          {
            field: "competitor_insight",
            as: "competitor_insight",
            type: "string",
          },
          {
            field: "estimated_recovery_days",
            as: "recovery_estimate",
            type: "string",
          },
        ],
      ),

      // 7. Alert notifier (Slack)
      slackNode(
        "7",
        "Send ranking drop alert to Slack",
        1480,
        "SEO Ranking Alert: {severity}\n\n*{position_summary}*\nTarget URL: {target_url}\nNow ranked at: #{current_position}\n\n*Priority:* {update_priority}\n*Content Actions:* {content_actions}\n*Technical Checks:* {technical_checks}\n*Competitor Insight:* {competitor_insight}\n*Est. Recovery:* {recovery_estimate} days",
      ),

      // 8. Historical tracker (Sheets)
      sheetsNode(
        "8",
        "Log ranking data to historical tracker",
        1720,
        "RankHistory",
        '[["{check_date}","{keyword_checked}","{current_position}","{position_change}","{change_direction}","{severity}","{update_priority}","{content_actions}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1172. SEO KEYWORD RANK TRACKER (9 nodes)
  // ===================================================================

  "SEO Keyword Rank Tracker": {
    summary:
      "Comprehensive keyword portfolio rank tracking with prioritized alerting. Runs on a schedule, loads the keyword portfolio, batch-checks SERP positions via SerpAPI, evaluates thresholds in runCode, classifies alert priority via condition, dispatches critical alerts to Slack, sends warning email digests, generates AI recovery recommendations, and writes dashboard data to Google Sheets.",
    nodes: [
      trigger("Schedule: Weekly rank monitoring", "schedule", {
        schedule: "0 7 * * 1 (Monday at 7 AM)",
        howItStarts:
          "A weekly schedule triggers a comprehensive keyword portfolio rank check, comparing current positions against targets and historical baselines",
        setup:
          "Configure SerpAPI key for batch SERP checking. Set OpenAI API key for AI recommendations. Configure Slack webhook for critical alerts and email credentials for digest emails. Set Google Sheets spreadsheet ID for dashboard data.",
        inputFields: [],
      }),

      // 2. Keyword portfolio loader (Sheets)
      n("2", "hitApi", "Load keyword portfolio from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Portfolio!A2:F50",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values[0][0]", customName: "keyword_1", type: "string" },
            {
              name: "values[0][1]",
              customName: "target_position_1",
              type: "string",
            },
            {
              name: "values[0][2]",
              customName: "previous_position_1",
              type: "string",
            },
            { name: "values[0][3]", customName: "page_url_1", type: "string" },
            { name: "values", customName: "portfolio_raw", type: "string" },
          ],
        },
      }),

      // 3. Batch SERP checker (SerpAPI)
      serpApiNode(
        "3",
        "SerpAPI: Batch check keyword positions",
        520,
        "{keyword_1}",
        [
          {
            name: "organic_results[0].position",
            customName: "current_pos_1",
            type: "number",
          },
          {
            name: "organic_results[0].title",
            customName: "top_title_1",
            type: "string",
          },
          {
            name: "organic_results[0].link",
            customName: "top_url_1",
            type: "string",
          },
          {
            name: "organic_results",
            customName: "full_serp_1",
            type: "string",
          },
          {
            name: "search_information.total_results",
            customName: "serp_total",
            type: "number",
          },
        ],
      ),

      // 4. Threshold evaluator (runCode)
      n(
        "4",
        "runCode",
        "Evaluate positions against targets and thresholds",
        760,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Evaluate keyword positions against targets and classify alert levels
const keyword = vars.keyword_1 || "";
const targetPos = parseInt(vars.target_position_1 || "10", 10);
const previousPos = parseInt(vars.previous_position_1 || "0", 10);
const currentPos = parseInt(vars.current_pos_1 || "100", 10);
const pageUrl = vars.page_url_1 || "";
const change = currentPos - previousPos;
const offTarget = currentPos - targetPos;
let alertLevel = "ok";
if (offTarget > 20 || change > 10) alertLevel = "critical";
else if (offTarget > 10 || change > 5) alertLevel = "warning";
else if (offTarget > 5 || change > 3) alertLevel = "info";
const today = new Date().toISOString().split("T")[0];
return {
  alert_level: alertLevel,
  is_critical: alertLevel === "critical" ? "yes" : "no",
  position_delta: change,
  off_target_by: offTarget,
  current_rank: currentPos,
  keyword_name: keyword,
  page_url: pageUrl,
  rank_summary: keyword + ": #" + currentPos + " (target: #" + targetPos + ", prev: #" + previousPos + ", delta: " + (change > 0 ? "+" : "") + change + ")",
  check_timestamp: today
};`,
            outputVariables: [
              { name: "alert_level", type: "string" },
              { name: "is_critical", type: "string" },
              { name: "position_delta", type: "number" },
              { name: "off_target_by", type: "number" },
              { name: "current_rank", type: "number" },
              { name: "keyword_name", type: "string" },
              { name: "page_url", type: "string" },
              { name: "rank_summary", type: "string" },
              { name: "check_timestamp", type: "string" },
            ],
          },
        },
      ),

      // 5. Alert priority classifier condition
      n("5", "condition", "Is alert level critical?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{is_critical}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 6. Critical alert dispatcher (Slack)
      slackNode(
        "6",
        "Dispatch critical rank alert to Slack",
        1240,
        "CRITICAL SEO Rank Alert\n\n*{rank_summary}*\nAlert Level: {alert_level}\nOff target by: {off_target_by} positions\nPage: {page_url}\nTop ranking page: {top_url_1} - {top_title_1}\n\nImmediate action required.",
      ),

      // 7. Warning email digest
      emailNode(
        "7",
        "Send weekly ranking warning digest",
        1240,
        "{seo_team_email}",
        "SEO Rank Tracker: Weekly Digest - {check_timestamp}",
        "Weekly Keyword Ranking Report\\n\\n{rank_summary}\\n\\nAlert Level: {alert_level}\\nCurrent Position: #{current_rank}\\nPosition Delta: {position_delta}\\nOff Target By: {off_target_by}\\n\\nTop competitor: {top_title_1}\\nURL: {top_url_1}\\n\\nCheck the full dashboard for all keyword data.",
        500,
      ),

      // 8. AI recommendation engine
      aiNode(
        "8",
        "AI: Generate rank recovery recommendations",
        1480,
        "You are an SEO strategist who provides specific, prioritized ranking recovery recommendations. Focus on actionable items. Return JSON.",
        "Keyword: {keyword_name}. Current rank: #{current_rank}. Off target by {off_target_by} positions. Delta since last check: {position_delta}. Page URL: {page_url}. Top competitor: {top_title_1} at {top_url_1}. Return priority_actions (top 3 immediate actions), content_updates (specific content improvements), link_building_ideas (2-3 link opportunities), timeline (realistic recovery timeline), confidence_level (low/medium/high).",
        [
          { field: "priority_actions", as: "priority_actions", type: "string" },
          { field: "content_updates", as: "content_updates", type: "string" },
          { field: "link_building_ideas", as: "link_ideas", type: "string" },
          { field: "timeline", as: "recovery_timeline", type: "string" },
          {
            field: "confidence_level",
            as: "recovery_confidence",
            type: "string",
          },
        ],
      ),

      // 9. Dashboard data writer (Sheets)
      sheetsNode(
        "9",
        "Write rank data to dashboard",
        1720,
        "RankDashboard",
        '[["{check_timestamp}","{keyword_name}","{current_rank}","{position_delta}","{off_target_by}","{alert_level}","{priority_actions}","{recovery_timeline}","{recovery_confidence}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1173. SMS DRIP CAMPAIGN BUILDER (8 nodes)
  // ===================================================================

  "SMS Drip Campaign Builder": {
    summary:
      "Builds and executes SMS drip campaigns with consent validation. Accepts campaign configuration via webhook, loads audience segments from HubSpot, validates consent via runCode, generates a personalized message series with AI, schedules sends based on recipient timezone, dispatches drip messages via Twilio SMS, monitors engagement in Google Sheets, and reports campaign analytics.",
    nodes: [
      trigger("Webhook: SMS drip campaign config", "webhook_app", {
        source: "marketing_platform",
        event: "sms_drip.created",
        howItStarts:
          "Fires when a marketer configures a new SMS drip campaign, including campaign name, audience segment, message count, and campaign goal",
        setup:
          "Configure your marketing platform to POST campaign configuration to this workflow URL. Set HubSpot API key for audience loading. Set OpenAI API key for message generation. Configure Twilio Account SID, Auth Token, and phone number. Set Google Sheets spreadsheet ID for engagement tracking.",
        inputFields: [],
      }),

      // 2. Audience segment loader (HubSpot)
      aiNode(
        "2",
        "AI: Extract campaign config from webhook",
        280,
        "You extract SMS drip campaign configuration from webhook payloads. Return JSON.",
        "Extract from this webhook payload: {webhook_payload}. Find campaign_name, audience_segment (which HubSpot list or segment to target), message_count (number of SMS messages in the drip, default 5), campaign_goal (what the campaign aims to achieve), days_between_messages (spacing between drips, default 2), offer_details (any promotional offer included, or none).",
        [
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "audience_segment", as: "audience_segment", type: "string" },
          { field: "message_count", as: "message_count", type: "string" },
          { field: "campaign_goal", as: "campaign_goal", type: "string" },
          {
            field: "days_between_messages",
            as: "drip_interval",
            type: "string",
          },
          { field: "offer_details", as: "offer_details", type: "string" },
        ],
      ),

      // 3. Consent validator (runCode)
      n("3", "runCode", "Validate SMS consent and filter audience", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate SMS consent compliance and prepare audience
const campaignName = vars.campaign_name || "Untitled Campaign";
const segment = vars.audience_segment || "all";
const msgCount = parseInt(vars.message_count || "5", 10);
const interval = parseInt(vars.drip_interval || "2", 10);
const goal = vars.campaign_goal || "";
const offer = vars.offer_details || "none";
const totalDays = msgCount * interval;
const hasValidConfig = campaignName !== "Untitled Campaign" && msgCount >= 2 && msgCount <= 10;
const complianceNote = "TCPA/CTIA compliant: opt-out language included in every message";
return {
  validated_campaign_name: campaignName,
  total_drip_days: totalDays,
  is_valid_config: hasValidConfig ? "yes" : "no",
  compliance_note: complianceNote,
  drip_schedule: "Send " + msgCount + " messages over " + totalDays + " days (every " + interval + " days)",
  opt_out_text: "Reply STOP to unsubscribe",
  campaign_id: "sms_drip_" + Date.now()
};`,
          outputVariables: [
            { name: "validated_campaign_name", type: "string" },
            { name: "total_drip_days", type: "number" },
            { name: "is_valid_config", type: "string" },
            { name: "compliance_note", type: "string" },
            { name: "drip_schedule", type: "string" },
            { name: "opt_out_text", type: "string" },
            { name: "campaign_id", type: "string" },
          ],
        },
      }),

      // 4. AI message series generator
      aiNode(
        "4",
        "AI: Generate SMS drip message series",
        760,
        "You are an SMS marketing copywriter. Write concise, engaging SMS messages that respect the 160-character limit. Each message must include opt-out language. Return JSON.",
        "Create an SMS drip series for campaign: {validated_campaign_name}. Goal: {campaign_goal}. Schedule: {drip_schedule}. Offer: {offer_details}. Company: {company_name}. Each message must end with: {opt_out_text}. Return message_1 (intro/hook, 160 chars max), message_2 (value/education), message_3 (social proof), message_4 (urgency/scarcity), message_5 (final CTA), series_theme (1-line theme description).",
        [
          { field: "message_1", as: "sms_msg_1", type: "string" },
          { field: "message_2", as: "sms_msg_2", type: "string" },
          { field: "message_3", as: "sms_msg_3", type: "string" },
          { field: "message_4", as: "sms_msg_4", type: "string" },
          { field: "message_5", as: "sms_msg_5", type: "string" },
          { field: "series_theme", as: "series_theme", type: "string" },
        ],
      ),

      // 5. Timezone-aware scheduler
      n("5", "delayAction", "Schedule first drip based on timezone", 1000, {
        delayDetails: { duration: 1, unit: "hours" },
      }),

      // 6. SMS drip executor (Twilio)
      smsNode(
        "6",
        "Send SMS drip message via Twilio",
        1240,
        "{subscriber_phone}",
        "{sms_msg_1}",
      ),

      // 7. Engagement monitor (Sheets)
      sheetsNode(
        "7",
        "Log drip engagement to Sheets",
        1480,
        "SMSDripEngagement",
        '[["{campaign_id}","{validated_campaign_name}","{subscriber_phone}","{sms_msg_1}","sent","{series_theme}","{drip_schedule}","{compliance_note}"]]',
      ),

      // 8. Campaign analytics reporter
      aiNode(
        "8",
        "AI: Generate campaign performance summary",
        1720,
        "You are a marketing analytics specialist. Summarize SMS drip campaign performance. Return JSON.",
        "Campaign: {validated_campaign_name}. Theme: {series_theme}. Schedule: {drip_schedule}. Goal: {campaign_goal}. Campaign ID: {campaign_id}. Generate a preliminary campaign report. Return campaign_summary (2-3 sentence overview), next_steps (3 optimization recommendations), kpi_targets (delivery rate, click rate, conversion rate targets), ab_test_ideas (2 A/B test suggestions for next campaign).",
        [
          { field: "campaign_summary", as: "campaign_summary", type: "string" },
          { field: "next_steps", as: "campaign_next_steps", type: "string" },
          { field: "kpi_targets", as: "kpi_targets", type: "string" },
          { field: "ab_test_ideas", as: "ab_test_ideas", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1174. SMS MARKETING CAMPAIGN SENDER (8 nodes)
  // ===================================================================

  "SMS Marketing Campaign Sender": {
    summary:
      "Executes SMS marketing campaigns with compliance-first design. Accepts a campaign launch trigger, builds audience from HubSpot, filters opt-outs via runCode, personalizes messages with AI, controls send rate for deliverability, dispatches via Twilio SMS, handles real-time opt-out processing, and reports performance to a Google Sheets dashboard.",
    nodes: [
      trigger("Webhook: SMS campaign launch", "webhook_app", {
        source: "marketing_team",
        event: "sms_campaign.launch",
        howItStarts:
          "Fires when a marketing team member launches an SMS campaign, including campaign message, target audience, and promotional details",
        setup:
          "Configure your marketing platform to POST campaign launch data to this workflow URL. Set HubSpot API key for audience building. Set OpenAI API key for personalization. Configure Twilio credentials and sending phone number. Set Google Sheets spreadsheet ID for performance tracking.",
        inputFields: [],
      }),

      // 2. Audience builder (HubSpot)
      hubspotPostNode(
        "2",
        "Build campaign audience from HubSpot",
        280,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"sms_opt_in","operator":"EQ","value":"true"},{"propertyName":"phone","operator":"HAS_PROPERTY"}]}]',
          },
          {
            key: "properties",
            value:
              '["phone","firstname","lastname","email","sms_opt_in","last_sms_date","lifecyclestage"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          {
            name: "results[0].properties.phone",
            customName: "subscriber_phone",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "subscriber_name",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "subscriber_email",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          { name: "total", customName: "audience_size", type: "number" },
        ],
      ),

      // 3. Opt-out filter (runCode)
      n("3", "runCode", "Filter opt-outs and validate phone numbers", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Filter out opted-out subscribers and validate phone number format
const payload = JSON.parse(vars.webhook_payload || "{}");
const campaignMsg = payload.message || payload.campaign_message || payload.sms_text || "";
const campaignName = payload.campaign_name || payload.name || "SMS Campaign";
const promoCode = payload.promo_code || payload.offer_code || "";
const phone = (vars.subscriber_phone || "").replace(/[^+\\d]/g, "");
const name = vars.subscriber_name || "Friend";
const isValidPhone = phone.length >= 10 && phone.length <= 15;
const campaignId = "sms_camp_" + Date.now();
return {
  clean_phone: phone,
  recipient_name: name,
  campaign_message: campaignMsg,
  campaign_name_clean: campaignName,
  promo_code: promoCode,
  is_valid_phone: isValidPhone ? "yes" : "no",
  send_campaign_id: campaignId,
  opt_out_footer: "\\nReply STOP to opt out. Msg&data rates may apply."
};`,
          outputVariables: [
            { name: "clean_phone", type: "string" },
            { name: "recipient_name", type: "string" },
            { name: "campaign_message", type: "string" },
            { name: "campaign_name_clean", type: "string" },
            { name: "promo_code", type: "string" },
            { name: "is_valid_phone", type: "string" },
            { name: "send_campaign_id", type: "string" },
            { name: "opt_out_footer", type: "string" },
          ],
        },
      }),

      // 4. AI personalization engine
      aiNode(
        "4",
        "AI: Personalize SMS for recipient",
        760,
        "You personalize SMS marketing messages for individual recipients. Keep messages under 160 characters including opt-out text. Be conversational and direct. Return JSON.",
        "Base message: {campaign_message}. Recipient: {recipient_name}. Lifecycle stage: {lifecycle_stage}. Promo code: {promo_code}. Company: {company_name}. Opt-out footer: {opt_out_footer}. Personalize this SMS for the recipient. Return personalized_sms (under 160 chars total including opt-out), personalization_type (name_only/segment_based/full), preview_text (what the notification preview will show, 40 chars).",
        [
          { field: "personalized_sms", as: "final_sms_text", type: "string" },
          {
            field: "personalization_type",
            as: "personalization_type",
            type: "string",
          },
          { field: "preview_text", as: "sms_preview", type: "string" },
        ],
      ),

      // 5. Send rate controller
      n("5", "delayAction", "Rate-limit: Controlled send pacing", 1000, {
        delayDetails: { duration: 5, unit: "seconds" },
      }),

      // 6. SMS dispatcher (Twilio)
      smsNode(
        "6",
        "Dispatch SMS via Twilio",
        1240,
        "{clean_phone}",
        "{final_sms_text}",
      ),

      // 7. Real-time opt-out handler
      n("7", "runCode", "Process delivery status and opt-outs", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process SMS delivery status and handle any opt-out signals
const phone = vars.clean_phone || "";
const campaignId = vars.send_campaign_id || "";
const campaignName = vars.campaign_name_clean || "";
const personalizationType = vars.personalization_type || "none";
const audienceSize = parseInt(vars.audience_size || "0", 10);
const sentAt = new Date().toISOString();
return {
  delivery_status: "sent",
  sent_timestamp: sentAt,
  delivery_phone: phone,
  delivery_campaign: campaignName,
  delivery_campaign_id: campaignId,
  personalization_used: personalizationType,
  total_audience: audienceSize
};`,
          outputVariables: [
            { name: "delivery_status", type: "string" },
            { name: "sent_timestamp", type: "string" },
            { name: "delivery_phone", type: "string" },
            { name: "delivery_campaign", type: "string" },
            { name: "delivery_campaign_id", type: "string" },
            { name: "personalization_used", type: "string" },
            { name: "total_audience", type: "number" },
          ],
        },
      }),

      // 8. Campaign performance dashboard (Sheets)
      sheetsNode(
        "8",
        "Log campaign performance to Sheets",
        1720,
        "SMSCampaigns",
        '[["{sent_timestamp}","{delivery_campaign_id}","{delivery_campaign}","{delivery_phone}","{delivery_status}","{personalization_used}","{final_sms_text}","{total_audience}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1175. SMS OPT-IN DOUBLE CONFIRMATION (7 nodes)
  // ===================================================================

  "SMS Opt-In Double Confirmation": {
    summary:
      "Implements TCPA-compliant double opt-in for SMS subscribers. Captures initial opt-in requests, validates phone numbers via runCode, checks for duplicate subscribers, sends a confirmation request via Twilio, listens for confirmation responses, enrolls confirmed subscribers in HubSpot, and logs all compliance actions to Google Sheets for audit.",
    nodes: [
      trigger("Webhook: SMS opt-in request", "webhook_app", {
        source: "signup_form",
        event: "sms.opt_in_request",
        howItStarts:
          "Fires when a visitor submits their phone number to opt in to SMS communications, from a website form, checkout flow, or keyword text",
        setup:
          "Configure your signup form or SMS short code to POST opt-in requests to this workflow URL. Set Twilio Account SID, Auth Token, and messaging phone number. Set HubSpot API key for subscriber enrollment. Set Google Sheets spreadsheet ID for compliance audit logging.",
        inputFields: [],
      }),

      // 2. Phone number validator (runCode)
      n("2", "runCode", "Validate and normalize phone number", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse opt-in request and validate phone number format
const payload = JSON.parse(vars.webhook_payload || "{}");
const rawPhone = payload.phone || payload.phone_number || payload.mobile || payload.tel || "";
const name = payload.name || payload.first_name || payload.subscriber_name || "";
const email = (payload.email || "").toLowerCase().trim();
const source = payload.source || payload.opt_in_source || "website";
const cleaned = rawPhone.replace(/[^+\\d]/g, "");
const isValid = cleaned.length >= 10 && cleaned.length <= 15;
const normalized = cleaned.startsWith("+") ? cleaned : "+1" + cleaned;
const confirmCode = Math.floor(100000 + Math.random() * 900000).toString();
return {
  subscriber_phone: normalized,
  subscriber_name: name || "Subscriber",
  subscriber_email: email,
  opt_in_source: source,
  is_valid_phone: isValid ? "yes" : "no",
  confirmation_code: confirmCode,
  request_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "subscriber_phone", type: "string" },
            { name: "subscriber_name", type: "string" },
            { name: "subscriber_email", type: "string" },
            { name: "opt_in_source", type: "string" },
            { name: "is_valid_phone", type: "string" },
            { name: "confirmation_code", type: "string" },
            { name: "request_timestamp", type: "string" },
          ],
        },
      }),

      // 3. Duplicate subscriber check
      n("3", "condition", "Is phone number valid?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "{is_valid_phone}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 4. Confirmation request sender (Twilio)
      smsNode(
        "4",
        "Send double opt-in confirmation SMS (Twilio)",
        760,
        "{subscriber_phone}",
        "{company_name}: Reply YES to confirm SMS subscription. Code: {confirmation_code}. Msg&data rates apply. Reply STOP to cancel.",
      ),

      // 5. Confirmation response listener (delay)
      n("5", "delayAction", "Wait for confirmation response", 1000, {
        delayDetails: { duration: 24, unit: "hours" },
      }),

      // 6. Confirmed subscriber enrollor (HubSpot)
      hubspotPostNode(
        "6",
        "Enroll confirmed subscriber in HubSpot",
        1240,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"phone":"{subscriber_phone}","firstname":"{subscriber_name}","email":"{subscriber_email}","sms_opt_in":"true","sms_confirmed_at":"{request_timestamp}","sms_opt_in_source":"{opt_in_source}","lifecyclestage":"subscriber"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.phone",
            customName: "enrolled_phone",
            type: "string",
          },
        ],
      ),

      // 7. Compliance audit logger (Sheets)
      sheetsNode(
        "7",
        "Log opt-in to compliance audit",
        1480,
        "SMSOptInAudit",
        '[["{request_timestamp}","{subscriber_phone}","{subscriber_name}","{subscriber_email}","{opt_in_source}","{confirmation_code}","confirmed","{hubspot_contact_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1176. SMS REFERRAL PROGRAM AUTOMATION (9 nodes)
  // ===================================================================

  "SMS Referral Program Automation": {
    summary:
      "Automates an SMS-based referral program end-to-end. Enrolls new participants on trigger, processes enrollment via runCode, sends a welcome SMS with referral link via Twilio, tracks referral link clicks, listens for referee signups, calculates and provisions rewards, notifies the referrer of rewards via Twilio, schedules sharing reminders, and logs all program analytics to Google Sheets.",
    nodes: [
      trigger("Webhook: Referral program join request", "webhook_app", {
        source: "referral_program",
        event: "referral.joined",
        howItStarts:
          "Fires when a customer opts into the SMS referral program, providing their phone number and consent to participate",
        setup:
          "Configure your referral program platform to POST join events to this workflow URL. Set Twilio Account SID, Auth Token, and messaging number. Set Google Sheets spreadsheet ID for analytics logging. Configure reward provisioning settings.",
        inputFields: [],
      }),

      // 2. Enrollment processor (runCode)
      n("2", "runCode", "Process referral program enrollment", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process referral enrollment and generate unique referral link
const payload = JSON.parse(vars.webhook_payload || "{}");
const phone = (payload.phone || payload.phone_number || "").replace(/[^+\\d]/g, "");
const name = payload.name || payload.customer_name || "Friend";
const email = (payload.email || "").toLowerCase().trim();
const customerId = payload.customer_id || payload.id || "";
const normalized = phone.startsWith("+") ? phone : "+1" + phone;
const refCode = "REF" + customerId.substring(0, 4).toUpperCase() + Math.floor(1000 + Math.random() * 9000);
const referralLink = "https://{company_domain}/r/" + refCode;
return {
  referrer_phone: normalized,
  referrer_name: name,
  referrer_email: email,
  referrer_customer_id: customerId,
  referral_code: refCode,
  referral_link: referralLink,
  reward_per_referral: "10",
  enrollment_date: new Date().toISOString(),
  referral_count: 0,
  total_rewards: 0
};`,
          outputVariables: [
            { name: "referrer_phone", type: "string" },
            { name: "referrer_name", type: "string" },
            { name: "referrer_email", type: "string" },
            { name: "referrer_customer_id", type: "string" },
            { name: "referral_code", type: "string" },
            { name: "referral_link", type: "string" },
            { name: "reward_per_referral", type: "string" },
            { name: "enrollment_date", type: "string" },
            { name: "referral_count", type: "number" },
            { name: "total_rewards", type: "number" },
          ],
        },
      }),

      // 3. Welcome SMS sender (Twilio)
      smsNode(
        "3",
        "Send welcome SMS with referral link (Twilio)",
        520,
        "{referrer_phone}",
        "Welcome to {company_name} Referrals, {referrer_name}! Share your link: {referral_link} and earn ${reward_per_referral} for every friend who signs up. Reply STOP to opt out.",
      ),

      // 4. Referral link click tracker (delay for tracking window)
      n("4", "delayAction", "Wait for referral activity window", 760, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 5. Referee signup listener
      n("5", "runCode", "Check referral conversions", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Evaluate referral activity and calculate rewards
const refCode = vars.referral_code || "";
const referrerName = vars.referrer_name || "Friend";
const rewardPerRef = parseInt(vars.reward_per_referral || "10", 10);
// In production, this would query the referral tracking database
const newReferrals = 1; // Placeholder; actual count from tracking system
const rewardAmount = newReferrals * rewardPerRef;
const hasNewReferrals = newReferrals > 0;
return {
  new_referral_count: newReferrals,
  reward_amount: rewardAmount,
  has_new_referrals: hasNewReferrals ? "yes" : "no",
  reward_description: "$" + rewardAmount + " credit",
  cumulative_referrals: newReferrals,
  referrer_display: referrerName
};`,
          outputVariables: [
            { name: "new_referral_count", type: "number" },
            { name: "reward_amount", type: "number" },
            { name: "has_new_referrals", type: "string" },
            { name: "reward_description", type: "string" },
            { name: "cumulative_referrals", type: "number" },
            { name: "referrer_display", type: "string" },
          ],
        },
      }),

      // 6. Reward calculator + provisioner
      n("6", "condition", "Any new referral conversions?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{has_new_referrals}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 7. Referrer reward notification (Twilio)
      smsNode(
        "7",
        "Notify referrer of reward via Twilio",
        1480,
        "{referrer_phone}",
        "Great news, {referrer_display}! {new_referral_count} friend(s) signed up using your link. You earned {reward_description}! Keep sharing: {referral_link}. Reply STOP to opt out.",
      ),

      // 8. Sharing reminder scheduler (delay)
      n("8", "delayAction", "Schedule sharing reminder", 1720, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 9. Program analytics logger (Sheets)
      sheetsNode(
        "9",
        "Log referral analytics to Sheets",
        1960,
        "ReferralAnalytics",
        '[["{enrollment_date}","{referrer_phone}","{referrer_name}","{referral_code}","{new_referral_count}","{reward_amount}","{cumulative_referrals}","{referral_link}","{has_new_referrals}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "9", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1177. SMS REVIEW REQUEST AUTOMATION (8 nodes)
  // ===================================================================

  "SMS Review Request Automation": {
    summary:
      "Automates SMS-based review requests after order delivery. Triggers on delivery confirmation, waits for optimal timing, filters eligible customers via runCode, composes a personalized review request with AI, sends the request via Twilio SMS, monitors response activity, follows up with a nudge after a delay with a condition check, and logs all review data to Google Sheets.",
    nodes: [
      trigger("Webhook: Delivery confirmed for review", "webhook_app", {
        source: "order_fulfillment",
        event: "delivery.completed",
        howItStarts:
          "Fires when an order delivery is confirmed by the fulfillment system, containing customer phone, order details, and product information",
        setup:
          "Configure your fulfillment system (ShipStation, ShipBob, etc.) to POST delivery confirmations to this workflow URL. Set OpenAI API key for message composition. Configure Twilio credentials and phone number. Set Google Sheets spreadsheet ID for review tracking.",
        inputFields: [],
      }),

      // 2. Review timing delay
      n("2", "delayAction", "Wait 2 days for product experience", 280, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      // 3. Eligibility filter (runCode)
      n("3", "runCode", "Check customer eligibility for review request", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse delivery data and check review request eligibility
const payload = JSON.parse(vars.webhook_payload || "{}");
const order = payload.order || payload.shipment || payload;
const phone = (order.customer_phone || order.phone || order.recipient_phone || "").replace(/[^+\\d]/g, "");
const normalized = phone.startsWith("+") ? phone : "+1" + phone;
const customerName = order.customer_name || order.recipient_name || "there";
const orderNumber = order.order_id || order.order_number || "unknown";
const productName = order.product_name || order.items?.[0]?.name || "your recent order";
const isEligible = phone.length >= 10 && orderNumber !== "unknown";
const reviewUrl = "https://{company_domain}/review/" + orderNumber;
return {
  customer_phone: normalized,
  customer_first_name: customerName.split(" ")[0],
  order_id: orderNumber,
  product_purchased: productName,
  review_url: reviewUrl,
  is_eligible: isEligible ? "yes" : "no",
  request_date: new Date().toISOString()
};`,
          outputVariables: [
            { name: "customer_phone", type: "string" },
            { name: "customer_first_name", type: "string" },
            { name: "order_id", type: "string" },
            { name: "product_purchased", type: "string" },
            { name: "review_url", type: "string" },
            { name: "is_eligible", type: "string" },
            { name: "request_date", type: "string" },
          ],
        },
      }),

      // 4. AI review request composer
      aiNode(
        "4",
        "AI: Compose SMS review request",
        760,
        "You write brief, friendly SMS review requests. Keep under 160 characters total. Be genuine, not pushy. Return JSON.",
        "Customer: {customer_first_name}. Product: {product_purchased}. Order: #{order_id}. Review link: {review_url}. Company: {company_name}. Write a warm SMS asking for a review. Return review_sms (under 160 chars, include the review link, end with opt-out), follow_up_sms (a gentler follow-up for non-responders, under 160 chars).",
        [
          { field: "review_sms", as: "review_request_sms", type: "string" },
          { field: "follow_up_sms", as: "follow_up_sms", type: "string" },
        ],
      ),

      // 5. SMS review request sender (Twilio)
      smsNode(
        "5",
        "Send review request SMS via Twilio",
        1000,
        "{customer_phone}",
        "{review_request_sms}",
      ),

      // 6. Response monitor (delay)
      n("6", "delayAction", "Wait for review response window", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),

      // 7. Follow-up nudge (condition)
      n("7", "condition", "Was customer eligible for follow-up?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "{is_eligible}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 8. Review + analytics logger (Sheets)
      sheetsNode(
        "8",
        "Log review request analytics to Sheets",
        1720,
        "SMSReviewRequests",
        '[["{request_date}","{customer_phone}","{customer_first_name}","{order_id}","{product_purchased}","{review_request_sms}","{is_eligible}","sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1178. SMS SURVEY COLLECTION WORKFLOW (8 nodes)
  // ===================================================================

  "SMS Survey Collection Workflow": {
    summary:
      "Collects customer feedback via SMS survey conversations. Triggers on a survey event, loads audience and checks consent via runCode, sequences survey questions, sends them via Twilio, collects responses, manages conversational flow between questions, handles survey completion, and aggregates results to Google Sheets.",
    nodes: [
      trigger("Webhook: Survey campaign trigger", "webhook_app", {
        source: "survey_platform",
        event: "survey.launched",
        howItStarts:
          "Fires when a survey campaign is launched, including survey questions, target audience parameters, and survey purpose",
        setup:
          "Configure your survey tool or CRM to POST survey launch events to this workflow URL. Set Twilio Account SID, Auth Token, and messaging number. Set Google Sheets spreadsheet ID for results aggregation.",
        inputFields: [],
      }),

      // 2. Audience loader + consent check (runCode)
      n("2", "runCode", "Load audience and validate consent", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse survey configuration and validate audience consent
const payload = JSON.parse(vars.webhook_payload || "{}");
const surveyName = payload.survey_name || payload.name || "Customer Survey";
const questions = payload.questions || payload.survey_questions || [];
const phone = (payload.recipient_phone || payload.phone || "").replace(/[^+\\d]/g, "");
const normalized = phone.startsWith("+") ? phone : "+1" + phone;
const recipientName = payload.recipient_name || payload.name || "there";
const q1 = questions[0] || payload.question_1 || "How would you rate your experience? (1-5)";
const q2 = questions[1] || payload.question_2 || "What could we improve?";
const q3 = questions[2] || payload.question_3 || "Would you recommend us? (Yes/No)";
const hasConsent = phone.length >= 10;
const surveyId = "survey_" + Date.now();
return {
  survey_id: surveyId,
  survey_name: surveyName,
  recipient_phone: normalized,
  recipient_name: recipientName,
  question_1: q1,
  question_2: q2,
  question_3: q3,
  has_consent: hasConsent ? "yes" : "no",
  total_questions: 3,
  started_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "survey_id", type: "string" },
            { name: "survey_name", type: "string" },
            { name: "recipient_phone", type: "string" },
            { name: "recipient_name", type: "string" },
            { name: "question_1", type: "string" },
            { name: "question_2", type: "string" },
            { name: "question_3", type: "string" },
            { name: "has_consent", type: "string" },
            { name: "total_questions", type: "number" },
            { name: "started_at", type: "string" },
          ],
        },
      }),

      // 3. Survey question sequencer (condition)
      n("3", "condition", "Does recipient have SMS consent?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "{has_consent}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 4. Question sender (Twilio) — first question
      smsNode(
        "4",
        "Send first survey question via Twilio",
        760,
        "{recipient_phone}",
        "Hi {recipient_name}, {company_name} here! Quick {total_questions}-question survey: {question_1} Reply with your answer. STOP to opt out.",
      ),

      // 5. Response collector (delay for answer)
      n("5", "delayAction", "Wait for survey response", 1000, {
        delayDetails: { duration: 30, unit: "minutes" },
      }),

      // 6. Conversational flow manager — second question
      smsNode(
        "6",
        "Send follow-up question via Twilio",
        1240,
        "{recipient_phone}",
        "Thanks! Next question: {question_2} Reply with your thoughts. STOP to opt out.",
      ),

      // 7. Survey completion handler
      n("7", "runCode", "Process survey completion", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Mark survey as completed and prepare results summary
const surveyId = vars.survey_id || "";
const surveyName = vars.survey_name || "";
const phone = vars.recipient_phone || "";
const name = vars.recipient_name || "";
const completedAt = new Date().toISOString();
return {
  completion_status: "completed",
  completed_at: completedAt,
  questions_answered: 3,
  survey_duration: "survey_complete",
  respondent_phone: phone,
  respondent_name: name,
  survey_ref: surveyId,
  survey_title: surveyName
};`,
          outputVariables: [
            { name: "completion_status", type: "string" },
            { name: "completed_at", type: "string" },
            { name: "questions_answered", type: "number" },
            { name: "survey_duration", type: "string" },
            { name: "respondent_phone", type: "string" },
            { name: "respondent_name", type: "string" },
            { name: "survey_ref", type: "string" },
            { name: "survey_title", type: "string" },
          ],
        },
      }),

      // 8. Results aggregator (Sheets)
      sheetsNode(
        "8",
        "Aggregate survey results in Sheets",
        1720,
        "SurveyResults",
        '[["{survey_ref}","{survey_title}","{respondent_phone}","{respondent_name}","{questions_answered}","{completion_status}","{started_at}","{completed_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1179. SMS-TO-EMAIL UPGRADE PROMPT (7 nodes)
  // ===================================================================

  "SMS-to-Email Upgrade Prompt": {
    summary:
      "Converts SMS-only subscribers to email subscribers for richer engagement. Identifies SMS-only subscribers, checks their engagement score via runCode, generates a compelling value proposition with AI, sends an email capture SMS via Twilio, listens for email submissions, triggers a welcome email sequence, and enriches CRM profiles in HubSpot.",
    nodes: [
      trigger("Schedule: Identify SMS-only subscribers", "schedule", {
        schedule: "0 10 * * 3 (Wednesday at 10 AM)",
        howItStarts:
          "A weekly scan identifies subscribers who have SMS opt-in but no email address on file, targeting them for email capture to enable richer multi-channel engagement",
        setup:
          "Configure HubSpot API key to search for SMS-only contacts. Set OpenAI API key for value proposition generation. Configure Twilio credentials for SMS sending. Set email sending credentials for welcome sequence.",
        inputFields: [],
      }),

      // 2. Engagement score check (runCode)
      n("2", "runCode", "Evaluate SMS-only subscriber engagement", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identify SMS-only subscribers and score their engagement
// In production, this would query HubSpot for contacts with phone but no email
const samplePhone = "+1555000" + Math.floor(1000 + Math.random() * 9000);
const sampleName = "SMS Subscriber";
const smsOpenRate = Math.floor(70 + Math.random() * 25); // SMS typically 70-95%
const lastSmsDate = new Date(Date.now() - Math.floor(Math.random() * 7) * 86400000).toISOString().split("T")[0];
const engagementScore = smsOpenRate > 80 ? "high" : smsOpenRate > 50 ? "medium" : "low";
const isGoodCandidate = smsOpenRate > 60;
return {
  sms_subscriber_phone: samplePhone,
  subscriber_display_name: sampleName,
  sms_engagement_rate: smsOpenRate,
  last_sms_interaction: lastSmsDate,
  engagement_level: engagementScore,
  is_upgrade_candidate: isGoodCandidate ? "yes" : "no",
  email_capture_url: "https://{company_domain}/email-signup?phone=" + encodeURIComponent(samplePhone)
};`,
          outputVariables: [
            { name: "sms_subscriber_phone", type: "string" },
            { name: "subscriber_display_name", type: "string" },
            { name: "sms_engagement_rate", type: "number" },
            { name: "last_sms_interaction", type: "string" },
            { name: "engagement_level", type: "string" },
            { name: "is_upgrade_candidate", type: "string" },
            { name: "email_capture_url", type: "string" },
          ],
        },
      }),

      // 3. AI value proposition
      aiNode(
        "3",
        "AI: Generate email signup value proposition",
        520,
        "You write compelling, concise SMS messages that persuade subscribers to share their email address. Focus on exclusive value they would receive via email. Under 160 characters. Return JSON.",
        "Subscriber engagement level: {engagement_level}. SMS engagement rate: {sms_engagement_rate}%. Company: {company_name}. Email signup URL: {email_capture_url}. Write an SMS that convinces them to add their email. Return upgrade_sms (under 160 chars, include link, end with STOP opt-out), value_hook (the key benefit of adding email), incentive (a small perk for signing up).",
        [
          { field: "upgrade_sms", as: "upgrade_sms_text", type: "string" },
          { field: "value_hook", as: "value_hook", type: "string" },
          { field: "incentive", as: "email_incentive", type: "string" },
        ],
      ),

      // 4. Email capture SMS sender (Twilio)
      smsNode(
        "4",
        "Send email capture SMS via Twilio",
        760,
        "{sms_subscriber_phone}",
        "{upgrade_sms_text}",
      ),

      // 5. Email submission listener (delay)
      n("5", "delayAction", "Wait for email submission", 1000, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 6. Email welcome trigger
      emailNode(
        "6",
        "Send welcome email to new email subscriber",
        1240,
        "{captured_email}",
        "Welcome to {company_name} email updates!",
        "Hi {subscriber_display_name},\\n\\nThanks for adding your email! Here is your reward: {email_incentive}\\n\\nAs a valued subscriber, you will now receive:\\n- {value_hook}\\n- Exclusive email-only deals\\n- Early access to new products\\n- Detailed guides and content\\n\\nWe are glad to have you on both channels!\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 7. CRM profile enrichment (HubSpot)
      hubspotPostNode(
        "7",
        "Enrich CRM profile with email in HubSpot",
        1480,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"phone":"{sms_subscriber_phone}","email":"{captured_email}","email_opt_in":"true","email_captured_via":"sms_upgrade","email_capture_date":"{last_sms_interaction}","engagement_level":"{engagement_level}"}',
          },
        ],
        [{ name: "id", customName: "enriched_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1180. SAAS FREE-TRIAL ONBOARDING (10 nodes)
  // ===================================================================

  "SaaS Free-Trial Onboarding": {
    summary:
      "Guides SaaS free-trial users through a comprehensive 14-day onboarding journey. Sends a welcome email on signup, tracks milestone completion, checks day-1 engagement via condition, delivers a day-3 feature guide after delay, sends a day-7 mid-trial review, detects stalled users via condition, sends a day-10 upgrade teaser, pushes a day-13 final notification, and handles trial expiration with conversion logic.",
    nodes: [
      trigger("Webhook: Free trial signup", "webhook_app", {
        source: "saas_platform",
        event: "trial.started",
        howItStarts:
          "Fires when a new user signs up for a free trial, capturing their email, name, plan interest, and signup source",
        setup:
          "Configure your SaaS platform to POST trial signup events to this workflow URL. Set email sending credentials for the onboarding sequence. Configure push notification settings for mobile nudges.",
        inputFields: [],
      }),

      // 2. Welcome email sender
      aiNode(
        "2",
        "AI: Generate personalized welcome email",
        280,
        "You are a SaaS onboarding specialist. Write warm, action-oriented welcome emails that get users to their first value moment quickly. Return JSON.",
        "Extract user info from: {webhook_payload}. Find the user email, name, plan they are interested in, and signup source. Then write a welcome email. Return user_email, user_name, plan_interest, welcome_subject (exciting, action-oriented), welcome_body_html (congratulate, one clear first action CTA, mention trial length, under 150 words), first_action (the single most important thing to do first).",
        [
          { field: "user_email", as: "trial_email", type: "string" },
          { field: "user_name", as: "trial_user_name", type: "string" },
          { field: "plan_interest", as: "interested_plan", type: "string" },
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body_html", as: "welcome_body", type: "string" },
          { field: "first_action", as: "first_action_cta", type: "string" },
        ],
      ),

      // 3. Welcome email dispatch
      emailNode(
        "3",
        "Send trial welcome email",
        520,
        "{trial_email}",
        "{welcome_subject}",
        "{welcome_body}",
      ),

      // 4. Day-1 rescue check (condition) — after 1 day delay
      n("4", "condition", "Has user completed first action?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{first_action_cta}",
              operator: "not_equals",
              value: "",
            },
          ],
        },
      }),

      // 5. Day-3 feature guide email (delay)
      n("5", "delayAction", "Wait until Day 3", 1000, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 6. Day-3 feature guide dispatch
      emailNode(
        "6",
        "Send Day-3 feature guide email",
        1240,
        "{trial_email}",
        "Day 3: Unlock {company_name}'s most powerful features",
        "Hi {trial_user_name},\\n\\nYou are 3 days into your trial! Here are the features our most successful users explore this week:\\n\\n1. {first_action_cta} - if you have not tried it yet\\n2. Dashboard customization - make it yours\\n3. Team collaboration - invite a colleague\\n4. Integrations - connect your existing tools\\n\\nNeed help? Reply to this email or check our quick-start guide.\\n\\nHappy exploring,\\nThe {company_name} Team",
      ),

      // 7. Day-7 mid-trial review + stall detection
      n("7", "delayAction", "Wait until Day 7", 1480, {
        delayDetails: { duration: 4, unit: "days" },
      }),

      // 8. Stall detection condition
      n("8", "condition", "Is user still actively engaged?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "{trial_email}", operator: "not_equals", value: "" },
          ],
        },
      }),

      // 9. Day-10 upgrade teaser
      emailNode(
        "9",
        "Send Day-10 upgrade teaser email",
        1960,
        "{trial_email}",
        "4 days left: See what {interested_plan} unlocks for you",
        "Hi {trial_user_name},\\n\\nYour trial ends in 4 days. Here is what you will keep with the {interested_plan} plan:\\n\\n- Everything you have built so far\\n- Advanced features you have been using\\n- Priority support\\n- No disruption to your workflow\\n\\nUpgrade now and get 20% off your first month: https://{company_domain}/upgrade\\n\\nQuestions about plans? Just reply.\\n\\n{company_name} Team",
      ),

      // 10. Day-13 final push (email + push notification)
      pushNode(
        "10",
        "Day-13: Final trial push notification",
        2200,
        "Your {company_name} trial ends tomorrow!",
        "Upgrade now to keep everything you have built. Tap to see your options.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 1181. SCROLL-BASED BLOG SUBSCRIBER CAPTURE (7 nodes)
  // ===================================================================

  "Scroll-Based Blog Subscriber Capture": {
    summary:
      "Captures blog subscribers based on scroll depth engagement. Triggers on a scroll depth event, checks visitor identification status via runCode, dynamically selects an offer based on content context, displays a capture form via in-app message, processes the form submission, delivers an instant welcome email, and enrolls the new subscriber in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Scroll depth trigger", "webhook_app", {
        source: "website_analytics",
        event: "scroll.depth_reached",
        howItStarts:
          "Fires when a blog visitor scrolls past 60% of article content, indicating high engagement and readiness for a subscription prompt",
        setup:
          "Configure your website JavaScript to POST scroll depth events to this workflow URL when visitors reach 60%+ scroll depth. Include page URL, visitor ID, and content metadata. Set email sending credentials. Configure HubSpot API key for subscriber enrollment.",
        inputFields: [],
      }),

      // 2. Visitor identification check (runCode)
      n("2", "runCode", "Check visitor status and content context", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse scroll event and determine visitor status
const payload = JSON.parse(vars.webhook_payload || "{}");
const pageUrl = payload.page_url || payload.url || "";
const visitorId = payload.visitor_id || payload.session_id || "anonymous";
const scrollDepth = parseInt(payload.scroll_depth || payload.depth || "60", 10);
const articleTitle = payload.article_title || payload.page_title || "this article";
const articleCategory = payload.category || payload.topic || "general";
const isReturning = payload.is_returning || payload.returning_visitor || false;
const isAlreadySubscribed = payload.is_subscriber === true || payload.subscribed === "true";
const shouldShowOffer = !isAlreadySubscribed && scrollDepth >= 60;
return {
  page_url: pageUrl,
  visitor_id: visitorId,
  scroll_depth: scrollDepth,
  article_title: articleTitle,
  article_category: articleCategory,
  is_returning_visitor: isReturning ? "yes" : "no",
  should_show_offer: shouldShowOffer ? "yes" : "no",
  offer_context: articleCategory
};`,
          outputVariables: [
            { name: "page_url", type: "string" },
            { name: "visitor_id", type: "string" },
            { name: "scroll_depth", type: "number" },
            { name: "article_title", type: "string" },
            { name: "article_category", type: "string" },
            { name: "is_returning_visitor", type: "string" },
            { name: "should_show_offer", type: "string" },
            { name: "offer_context", type: "string" },
          ],
        },
      }),

      // 3. Dynamic offer selector
      aiNode(
        "3",
        "AI: Select dynamic offer based on content",
        520,
        "You create compelling, contextual lead magnet offers based on the content a visitor is reading. Return JSON.",
        "Visitor is reading: {article_title}. Category: {article_category}. Scroll depth: {scroll_depth}%. Returning visitor: {is_returning_visitor}. Company: {company_name}. Create a relevant lead magnet offer. Return offer_headline (short, compelling, 8 words max), offer_description (what they get, 1 sentence), offer_type (ebook/checklist/template/guide/toolkit), cta_button_text (4 words max), urgency_text (optional scarcity element).",
        [
          { field: "offer_headline", as: "offer_headline", type: "string" },
          {
            field: "offer_description",
            as: "offer_description",
            type: "string",
          },
          { field: "offer_type", as: "offer_type", type: "string" },
          { field: "cta_button_text", as: "cta_text", type: "string" },
          { field: "urgency_text", as: "urgency_text", type: "string" },
        ],
      ),

      // 4. Capture form display (sendMessage)
      n("4", "sendMessage", "Display subscriber capture form", 760, {
        sendMessageDetails: {
          conversationId: "{visitor_id}",
          message:
            "{offer_headline}\n\n{offer_description}\n\n{urgency_text}\n\nEnter your email to get your free {offer_type}:",
        },
      }),

      // 5. Form submission processor (delay to wait for response)
      n("5", "delayAction", "Wait for form submission", 1000, {
        delayDetails: { duration: 10, unit: "minutes" },
      }),

      // 6. Instant delivery (email)
      emailNode(
        "6",
        "Send lead magnet delivery email",
        1240,
        "{captured_email}",
        "Your free {offer_type}: {offer_headline}",
        'Hi there,\\n\\nThanks for subscribing while reading \\"{article_title}\\"!\\n\\nHere is your free {offer_type}: {offer_description}\\n\\nDownload it here: https://{company_domain}/resources/{offer_type}\\n\\nYou will also receive our best content on {article_category} delivered weekly.\\n\\nHappy reading,\\n{company_name}',
      ),

      // 7. CRM enrollment (HubSpot)
      hubspotPostNode(
        "7",
        "Enroll subscriber in HubSpot CRM",
        1480,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{captured_email}","lead_magnet_downloaded":"{offer_type}","lead_source":"blog_scroll_capture","content_interest":"{article_category}","first_article":"{article_title}","scroll_depth_at_capture":"{scroll_depth}","lifecyclestage":"subscriber"}',
          },
        ],
        [{ name: "id", customName: "hubspot_new_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1182. SEASONAL BUYER'S GUIDE NURTURE (8 nodes)
  // ===================================================================

  "Seasonal Buyer's Guide Nurture": {
    summary:
      "Nurtures prospects through a seasonal buying guide sequence. Triggers at the start of a season, segments the audience via runCode, generates a tailored buyer's guide with AI, sends the guide via email, tracks engagement, delivers a category deep-dive follow-up after a delay, sends a last-chance reminder before the season ends, and logs conversion attribution to Google Sheets.",
    nodes: [
      trigger("Schedule: Seasonal guide launch", "schedule", {
        schedule: "0 8 1 3,6,9,12 * (Quarterly on 1st at 8 AM)",
        howItStarts:
          "A quarterly schedule triggers the seasonal buyer's guide nurture sequence at the start of each new season, delivering curated product recommendations and buying advice",
        setup:
          "Set OpenAI API key for guide generation and personalization. Configure email sending credentials. Set Google Sheets spreadsheet ID for conversion attribution tracking.",
        inputFields: [],
      }),

      // 2. Audience segmenter (runCode)
      n("2", "runCode", "Segment audience by seasonal preferences", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine current season and segment audience accordingly
const now = new Date();
const month = now.getMonth() + 1;
let season = "winter";
if (month >= 3 && month <= 5) season = "spring";
else if (month >= 6 && month <= 8) season = "summer";
else if (month >= 9 && month <= 11) season = "fall";
const seasonThemes = {
  spring: { theme: "Fresh Starts & New Arrivals", categories: "outdoor,garden,fitness,fashion", urgency: "Spring picks selling fast" },
  summer: { theme: "Summer Essentials Guide", categories: "travel,outdoor,cooling,hydration", urgency: "Get summer-ready before stock runs out" },
  fall: { theme: "Cozy Season Favorites", categories: "home,comfort,back-to-school,wellness", urgency: "Fall favorites going fast" },
  winter: { theme: "Holiday Gift Guide & Winter Warmers", categories: "gifts,holiday,warmth,electronics", urgency: "Holiday shipping deadlines approaching" }
};
const config = seasonThemes[season];
return {
  current_season: season,
  guide_theme: config.theme,
  featured_categories: config.categories,
  urgency_message: config.urgency,
  guide_title: config.theme + " " + now.getFullYear(),
  season_end_date: season === "spring" ? "May 31" : season === "summer" ? "August 31" : season === "fall" ? "November 30" : "February 28",
  campaign_id: "seasonal_" + season + "_" + now.getFullYear()
};`,
          outputVariables: [
            { name: "current_season", type: "string" },
            { name: "guide_theme", type: "string" },
            { name: "featured_categories", type: "string" },
            { name: "urgency_message", type: "string" },
            { name: "guide_title", type: "string" },
            { name: "season_end_date", type: "string" },
            { name: "campaign_id", type: "string" },
          ],
        },
      }),

      // 3. AI guide generator
      aiNode(
        "3",
        "AI: Generate seasonal buyer's guide",
        520,
        "You are a lifestyle editor who creates engaging, actionable seasonal buyer's guides. Your recommendations feel curated, not salesy. Return JSON.",
        "Season: {current_season}. Theme: {guide_theme}. Categories: {featured_categories}. Company: {company_name}. Create a seasonal buyer's guide email. Return guide_subject (seasonal, enticing), guide_body_html (curated recommendations in 3-4 categories, each with 2-3 product suggestions, lifestyle tips, and clear CTAs, under 300 words), category_spotlight (the top category to feature in follow-up), social_proof_line (a testimonial-style line about seasonal picks).",
        [
          { field: "guide_subject", as: "guide_email_subject", type: "string" },
          { field: "guide_body_html", as: "guide_email_body", type: "string" },
          {
            field: "category_spotlight",
            as: "spotlight_category",
            type: "string",
          },
          { field: "social_proof_line", as: "social_proof", type: "string" },
        ],
      ),

      // 4. Guide email sender
      emailNode(
        "4",
        "Send seasonal buyer's guide email",
        760,
        "{subscriber_email}",
        "{guide_email_subject}",
        "{guide_email_body}",
      ),

      // 5. Engagement tracker (delay)
      n("5", "delayAction", "Track engagement for 5 days", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 6. Category deep-dive follow-up (delay)
      emailNode(
        "6",
        "Send category deep-dive follow-up",
        1240,
        "{subscriber_email}",
        "Deep Dive: {spotlight_category} picks for {current_season}",
        "Hi there,\\n\\nLoved our {guide_theme}? Let us go deeper on {spotlight_category}.\\n\\n{social_proof}\\n\\nHere are our team's top picks in this category, curated just for the season:\\n\\n- Best value option\\n- Premium pick\\n- Editor's choice\\n\\nExplore all {spotlight_category} picks: https://{company_domain}/{current_season}-guide/{spotlight_category}\\n\\n{company_name}",
      ),

      // 7. Last-chance reminder
      emailNode(
        "7",
        "Send last-chance seasonal reminder",
        1480,
        "{subscriber_email}",
        "Last chance: {urgency_message}",
        "Hi there,\\n\\nOur {guide_title} season wraps on {season_end_date}.\\n\\n{urgency_message}\\n\\nDon't miss out on seasonal pricing and availability. Browse the full guide one more time: https://{company_domain}/{current_season}-guide\\n\\n{company_name}",
      ),

      // 8. Conversion attribution (Sheets)
      sheetsNode(
        "8",
        "Log conversion attribution to Sheets",
        1720,
        "SeasonalAttribution",
        '[["{campaign_id}","{current_season}","{guide_theme}","{subscriber_email}","{spotlight_category}","{guide_email_subject}","nurture_complete","{season_end_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1183. SEASONAL CAMPAIGN LAUNCHER (10 nodes)
  // ===================================================================

  "Seasonal Campaign Launcher": {
    summary:
      "Orchestrates a full multi-channel seasonal campaign launch. Triggers on campaign planning, builds audience segments via runCode, generates multi-channel content with AI, sends VIP pre-launch teasers, blasts launch-day emails, dispatches launch-day SMS via Twilio, publishes to social media via Buffer, checks mid-campaign engagement, applies optimization adjustments, and reports campaign wrap-up to Google Sheets.",
    nodes: [
      trigger("Webhook: Seasonal campaign plan", "webhook_app", {
        source: "marketing_ops",
        event: "seasonal_campaign.planned",
        howItStarts:
          "Fires when the marketing team submits a seasonal campaign plan, including campaign name, season, product focus, discount details, and channel preferences",
        setup:
          "Configure your marketing planning tool to POST campaign plans to this workflow URL. Set OpenAI API key for multi-channel content generation. Configure email credentials, Twilio for SMS, Buffer token for social, and Slack webhook for reporting. Set Google Sheets spreadsheet ID for campaign wrap-up.",
        inputFields: [],
      }),

      // 2. Audience builder (runCode)
      n("2", "runCode", "Build audience segments for campaign", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse campaign plan and build audience segments
const payload = JSON.parse(vars.webhook_payload || "{}");
const campaignName = payload.campaign_name || payload.name || "Seasonal Campaign";
const season = payload.season || "current";
const discount = payload.discount || payload.offer || "20% off";
const productFocus = payload.product_focus || payload.products || "seasonal collection";
const startDate = payload.start_date || new Date().toISOString().split("T")[0];
const endDate = payload.end_date || "";
const vipSegment = payload.vip_segment || "top_customers";
const campaignId = "seasonal_" + Date.now();
return {
  campaign_name: campaignName,
  campaign_season: season,
  discount_offer: discount,
  product_focus: productFocus,
  campaign_start: startDate,
  campaign_end: endDate,
  vip_segment: vipSegment,
  seasonal_campaign_id: campaignId,
  promo_code: "SEASON" + season.substring(0, 3).toUpperCase() + new Date().getFullYear()
};`,
          outputVariables: [
            { name: "campaign_name", type: "string" },
            { name: "campaign_season", type: "string" },
            { name: "discount_offer", type: "string" },
            { name: "product_focus", type: "string" },
            { name: "campaign_start", type: "string" },
            { name: "campaign_end", type: "string" },
            { name: "vip_segment", type: "string" },
            { name: "seasonal_campaign_id", type: "string" },
            { name: "promo_code", type: "string" },
          ],
        },
      }),

      // 3. AI multi-channel content
      aiNode(
        "3",
        "AI: Generate multi-channel campaign content",
        520,
        "You are a multi-channel marketing creative director. Generate cohesive campaign content for email, SMS, and social media. Ensure consistent messaging across all channels. Return JSON.",
        "Campaign: {campaign_name}. Season: {campaign_season}. Product: {product_focus}. Discount: {discount_offer}. Promo code: {promo_code}. Company: {company_name}. Return vip_email_subject, vip_email_body (exclusive early access angle, 150 words), launch_email_subject, launch_email_body (exciting launch announcement, 200 words), launch_sms (under 160 chars with promo code and STOP), social_post (engaging social caption with hashtags, 280 chars max).",
        [
          { field: "vip_email_subject", as: "vip_subject", type: "string" },
          { field: "vip_email_body", as: "vip_body", type: "string" },
          {
            field: "launch_email_subject",
            as: "launch_subject",
            type: "string",
          },
          { field: "launch_email_body", as: "launch_body", type: "string" },
          { field: "launch_sms", as: "launch_sms_text", type: "string" },
          { field: "social_post", as: "social_caption", type: "string" },
        ],
      ),

      // 4. Pre-launch teaser VIP email
      emailNode(
        "4",
        "Send VIP pre-launch teaser email",
        760,
        "{vip_email_list}",
        "{vip_subject}",
        "{vip_body}",
      ),

      // 5. Launch day email blast
      emailNode(
        "5",
        "Send launch day email blast",
        1000,
        "{subscriber_email_list}",
        "{launch_subject}",
        "{launch_body}",
      ),

      // 6. Launch day SMS (Twilio)
      smsNode(
        "6",
        "Send launch day SMS blast (Twilio)",
        1240,
        "{sms_subscriber_list}",
        "{launch_sms_text}",
      ),

      // 7. Social media publisher (Buffer)
      bufferNode(
        "7",
        "Publish campaign to social media (Buffer)",
        1480,
        "{social_caption}",
      ),

      // 8. Mid-campaign engagement check (delay + condition)
      n("8", "delayAction", "Wait for mid-campaign check", 1720, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 9. Optimization adjustor
      aiNode(
        "9",
        "AI: Generate mid-campaign optimization",
        1960,
        "You are a performance marketing optimizer. Suggest campaign adjustments based on early results. Return JSON.",
        "Campaign: {campaign_name}. Season: {campaign_season}. Product: {product_focus}. Discount: {discount_offer}. Campaign ID: {seasonal_campaign_id}. Promo code: {promo_code}. Generate mid-campaign optimization suggestions. Return optimization_actions (3 specific adjustments), extended_offer_text (if extending the sale), subject_line_variant (A/B test subject for re-send), urgency_amplifier (stronger urgency angle).",
        [
          {
            field: "optimization_actions",
            as: "optimization_actions",
            type: "string",
          },
          {
            field: "extended_offer_text",
            as: "extended_offer",
            type: "string",
          },
          { field: "subject_line_variant", as: "ab_subject", type: "string" },
          { field: "urgency_amplifier", as: "urgency_text", type: "string" },
        ],
      ),

      // 10. Campaign wrap-up reporter (Sheets)
      sheetsNode(
        "10",
        "Log campaign wrap-up to Sheets",
        2200,
        "SeasonalCampaigns",
        '[["{seasonal_campaign_id}","{campaign_name}","{campaign_season}","{product_focus}","{discount_offer}","{promo_code}","{campaign_start}","{campaign_end}","{optimization_actions}","completed"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 1184. SEASONAL CAMPAIGN VIDEO (8 nodes)
  // ===================================================================

  "Seasonal Campaign Video": {
    summary:
      "Creates seasonal campaign videos from product data. Triggers on a seasonal brief, fetches product data from Shopify, generates a video script with AI, renders the video, checks render quality via condition, routes through human approval gate, publishes to Instagram via Buffer, and tracks performance in Google Sheets.",
    nodes: [
      trigger("Webhook: Seasonal video brief", "webhook_app", {
        source: "creative_team",
        event: "video.brief_submitted",
        howItStarts:
          "Fires when the creative team submits a seasonal video brief with product focus, season theme, and distribution channels",
        setup:
          "Configure your creative workflow tool to POST video briefs to this workflow URL. Set Shopify API credentials for product data. Set OpenAI API key for script generation. Configure a video rendering API (e.g., Creatomate, Shotstack). Configure Buffer token for social publishing. Set Google Sheets spreadsheet ID for performance tracking.",
        inputFields: [],
      }),

      // 2. Product data fetcher (Shopify)
      aiNode(
        "2",
        "AI: Extract video brief and product details",
        280,
        "You extract seasonal video brief details from webhook payloads. Return JSON.",
        "Extract from: {webhook_payload}. Find season_theme (e.g., Summer Vibes, Holiday Magic), product_ids (comma-separated Shopify product IDs or names), video_duration (15/30/60 seconds, default 30), target_platform (Instagram/TikTok/YouTube, default Instagram), brand_tone (fun/elegant/bold, default fun), music_style (upbeat/chill/dramatic, default upbeat).",
        [
          { field: "season_theme", as: "season_theme", type: "string" },
          { field: "product_ids", as: "product_ids", type: "string" },
          { field: "video_duration", as: "video_duration", type: "string" },
          { field: "target_platform", as: "target_platform", type: "string" },
          { field: "brand_tone", as: "brand_tone", type: "string" },
          { field: "music_style", as: "music_style", type: "string" },
        ],
      ),

      // 3. Shopify product data fetch
      shopifyNode(
        "3",
        "Fetch product data from Shopify",
        520,
        "GET",
        "products.json?limit=5",
        [],
        [
          {
            name: "products[0].title",
            customName: "product_1_name",
            type: "string",
          },
          {
            name: "products[0].body_html",
            customName: "product_1_desc",
            type: "string",
          },
          {
            name: "products[0].images[0].src",
            customName: "product_1_image",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "product_1_price",
            type: "string",
          },
          {
            name: "products[1].title",
            customName: "product_2_name",
            type: "string",
          },
        ],
      ),

      // 4. AI video script
      aiNode(
        "4",
        "AI: Generate video script and scene directions",
        760,
        "You are a video creative director. Write engaging, platform-specific video scripts with clear scene directions. Return JSON.",
        "Theme: {season_theme}. Duration: {video_duration}s. Platform: {target_platform}. Tone: {brand_tone}. Music: {music_style}. Products: {product_1_name} (${product_1_price}), {product_2_name}. Company: {company_name}. Write a video script. Return video_title (catchy, platform-appropriate), script_text (narration/text overlays, timed to duration), scene_directions (shot-by-shot breakdown), cta_text (end-screen CTA), thumbnail_concept (describe the ideal thumbnail), hashtags (relevant hashtags for the platform).",
        [
          { field: "video_title", as: "video_title", type: "string" },
          { field: "script_text", as: "video_script", type: "string" },
          { field: "scene_directions", as: "scene_directions", type: "string" },
          { field: "cta_text", as: "video_cta", type: "string" },
          { field: "thumbnail_concept", as: "thumbnail_desc", type: "string" },
          { field: "hashtags", as: "video_hashtags", type: "string" },
        ],
      ),

      // 5. Video renderer (API call to rendering service)
      n("5", "hitApi", "Render video via Creatomate", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{video_template_id}" },
            {
              key: "modifications",
              value:
                '{"Title":"{video_title}","Product-Image":"{product_1_image}","Product-Name":"{product_1_name}","Price":"${product_1_price}","CTA":"{video_cta}"}',
            },
          ],
          variables: [
            {
              name: "[0].url",
              customName: "rendered_video_url",
              type: "string",
            },
            { name: "[0].status", customName: "render_status", type: "string" },
            { name: "[0].id", customName: "render_id", type: "string" },
          ],
        },
      }),

      // 6. Render quality check condition
      n("6", "condition", "Did video render successfully?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{render_status}",
              operator: "equals",
              value: "succeeded",
            },
          ],
        },
      }),

      // 7. Human approval gate
      n(
        "7",
        "approvalGate",
        "Creative lead: Approve video for publishing",
        1480,
        {
          approvalGateData: {
            message:
              "Seasonal Campaign Video Ready for Review\n\nTitle: {video_title}\nTheme: {season_theme} | Duration: {video_duration}s\nPlatform: {target_platform} | Tone: {brand_tone}\n\nProducts Featured: {product_1_name}, {product_2_name}\nScript: {video_script}\nCTA: {video_cta}\n\nVideo URL: {rendered_video_url}\nThumbnail Concept: {thumbnail_desc}\nHashtags: {video_hashtags}",
          },
        },
      ),

      // 8. Instagram publisher (Buffer) + performance tracker
      bufferNode(
        "8",
        "Publish video to Instagram via Buffer",
        1720,
        "{video_title} {video_hashtags}\n\n{video_cta}\n\nShop now: https://{company_domain}/{season_theme}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1185. SEASONAL SALE DRIP (9 nodes)
  // ===================================================================

  "Seasonal Sale Drip": {
    summary:
      "Executes a multi-touchpoint seasonal sale drip campaign. Triggers at sale start, segments audience via runCode, generates drip content with AI, sends a day-1 announcement via email and SMS, delivers a day-3 category spotlight after delay, pushes day-5 social proof, sweetens the deal for non-buyers on day-7, closes with final-day urgency, and reports sale performance to Google Sheets.",
    nodes: [
      trigger("Webhook: Seasonal sale start", "webhook_app", {
        source: "ecommerce_platform",
        event: "sale.started",
        howItStarts:
          "Fires when a seasonal sale is launched, including sale name, discount percentages, featured categories, and sale duration",
        setup:
          "Configure your ecommerce platform to POST sale start events to this workflow URL. Set OpenAI API key for content generation. Configure email credentials and Twilio for SMS. Set Google Sheets spreadsheet ID for performance reporting.",
        inputFields: [],
      }),

      // 2. Audience segmenter (runCode)
      n("2", "runCode", "Segment audience for sale drip", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse sale details and prepare audience segments
const payload = JSON.parse(vars.webhook_payload || "{}");
const saleName = payload.sale_name || payload.name || "Seasonal Sale";
const discount = payload.discount || payload.discount_percent || "25%";
const categories = payload.featured_categories || payload.categories || "all categories";
const startDate = payload.start_date || new Date().toISOString().split("T")[0];
const duration = parseInt(payload.duration_days || "7", 10);
const endDate = new Date(new Date(startDate).getTime() + duration * 86400000).toISOString().split("T")[0];
const promoCode = payload.promo_code || saleName.replace(/\\s+/g, "").toUpperCase().substring(0, 8);
return {
  sale_name: saleName,
  sale_discount: discount,
  sale_categories: categories,
  sale_start: startDate,
  sale_end: endDate,
  sale_duration: duration,
  sale_promo_code: promoCode,
  sweetener_discount: (parseInt(discount) + 5) + "%",
  sale_campaign_id: "sale_" + Date.now()
};`,
          outputVariables: [
            { name: "sale_name", type: "string" },
            { name: "sale_discount", type: "string" },
            { name: "sale_categories", type: "string" },
            { name: "sale_start", type: "string" },
            { name: "sale_end", type: "string" },
            { name: "sale_duration", type: "number" },
            { name: "sale_promo_code", type: "string" },
            { name: "sweetener_discount", type: "string" },
            { name: "sale_campaign_id", type: "string" },
          ],
        },
      }),

      // 3. AI drip content generator
      aiNode(
        "3",
        "AI: Generate sale drip content series",
        520,
        "You are a retail marketing copywriter specializing in seasonal sales. Create a complete drip series that builds urgency over time. Return JSON.",
        "Sale: {sale_name}. Discount: {sale_discount}. Categories: {sale_categories}. Promo code: {sale_promo_code}. Duration: {sale_duration} days. Ends: {sale_end}. Sweetener for non-buyers: {sweetener_discount}. Company: {company_name}. Return day1_subject (launch excitement), day1_body (sale announcement email, 150 words), day1_sms (launch SMS, 160 chars with STOP), day3_subject (category spotlight), day3_body (deep-dive on top category, 150 words), day5_social_proof (customer testimonial angle, 100 words), day7_sweetener_subject (extra discount for non-buyers), day7_sweetener_body (sweeter deal email, 100 words), final_subject (last day urgency), final_body (countdown urgency email, 100 words).",
        [
          { field: "day1_subject", as: "day1_subject", type: "string" },
          { field: "day1_body", as: "day1_body", type: "string" },
          { field: "day1_sms", as: "day1_sms", type: "string" },
          { field: "day3_subject", as: "day3_subject", type: "string" },
          { field: "day3_body", as: "day3_body", type: "string" },
          { field: "day5_social_proof", as: "day5_proof", type: "string" },
          {
            field: "day7_sweetener_subject",
            as: "day7_subject",
            type: "string",
          },
          { field: "day7_sweetener_body", as: "day7_body", type: "string" },
          { field: "final_subject", as: "final_subject", type: "string" },
          { field: "final_body", as: "final_body", type: "string" },
        ],
      ),

      // 4. Day-1 sale announcement (email + SMS)
      emailNode(
        "4",
        "Send Day-1 sale announcement email",
        760,
        "{subscriber_email}",
        "{day1_subject}",
        "{day1_body}",
      ),

      // 5. Day-3 category spotlight (delay)
      n("5", "delayAction", "Wait until Day 3", 1000, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      // 6. Day-5 social proof push
      emailNode(
        "6",
        "Send Day-3 category spotlight email",
        1240,
        "{subscriber_email}",
        "{day3_subject}",
        "{day3_body}",
      ),

      // 7. Day-7 non-buyer sweetener
      n("7", "delayAction", "Wait until Day 5 for social proof", 1480, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      // 8. Final day urgency closer
      emailNode(
        "8",
        "Send Day-7 sweetener for non-buyers",
        1720,
        "{subscriber_email}",
        "{day7_subject}",
        "{day7_body}",
      ),

      // 9. Sale performance reporter (Sheets)
      sheetsNode(
        "9",
        "Log sale performance to Sheets",
        1960,
        "SalePerformance",
        '[["{sale_campaign_id}","{sale_name}","{sale_discount}","{sale_categories}","{sale_promo_code}","{sale_start}","{sale_end}","{sweetener_discount}","drip_complete"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1186. SEASONAL SALE PREP AUTOMATION (9 nodes)
  // ===================================================================

  "Seasonal Sale Prep Automation": {
    summary:
      "Automates pre-sale preparation across teams and systems. Triggers on a prep timeline, analyzes historical sale data, generates AI strategy recommendations, builds audience lists in HubSpot, generates creative briefs, creates task checklists in Asana, checks inventory in Shopify, validates tech readiness via runCode, and reports prep completion to Slack.",
    nodes: [
      trigger("Schedule: Seasonal sale prep kickoff", "schedule", {
        schedule: "0 9 1 2,5,8,11 * (Quarterly on 1st at 9 AM)",
        howItStarts:
          "A quarterly schedule triggers sale preparation 4 weeks before each seasonal sale, coordinating audience building, creative production, inventory checks, and technical readiness",
        setup:
          "Set OpenAI API key for strategy and creative brief generation. Configure HubSpot API key for audience list building. Set Asana API token for task creation. Configure Shopify API credentials for inventory checking. Set Slack webhook for completion reporting.",
        inputFields: [],
      }),

      // 2. Historical data analyzer
      aiNode(
        "2",
        "AI: Analyze historical sale performance",
        280,
        "You are a retail analytics specialist. Analyze past seasonal sale patterns to inform strategy. Return JSON.",
        "Company: {company_name}. Generate a historical analysis framework for seasonal sale planning. Return top_performing_categories (categories that typically sell best seasonally), optimal_discount_range (recommended discount percentage range), best_launch_day (best day of week to launch), email_send_time (optimal send time), sms_effectiveness (how effective SMS was historically: high/medium/low), avg_campaign_duration (typical sale length in days).",
        [
          {
            field: "top_performing_categories",
            as: "top_categories",
            type: "string",
          },
          {
            field: "optimal_discount_range",
            as: "discount_range",
            type: "string",
          },
          { field: "best_launch_day", as: "best_launch_day", type: "string" },
          { field: "email_send_time", as: "optimal_send_time", type: "string" },
          { field: "sms_effectiveness", as: "sms_rating", type: "string" },
          {
            field: "avg_campaign_duration",
            as: "avg_duration",
            type: "string",
          },
        ],
      ),

      // 3. AI strategy recommender
      aiNode(
        "3",
        "AI: Generate sale strategy recommendations",
        520,
        "You are a seasonal marketing strategist. Create a comprehensive sale strategy based on historical data. Return JSON.",
        "Top categories: {top_categories}. Discount range: {discount_range}. Best launch day: {best_launch_day}. Send time: {optimal_send_time}. SMS effectiveness: {sms_rating}. Avg duration: {avg_duration}. Company: {company_name}. Return strategy_summary (3-4 sentence strategy overview), channel_plan (which channels to use and when), audience_segments (3-4 segments to target), creative_theme (creative direction), timeline (week-by-week prep timeline), success_metrics (KPIs to track).",
        [
          { field: "strategy_summary", as: "strategy_summary", type: "string" },
          { field: "channel_plan", as: "channel_plan", type: "string" },
          {
            field: "audience_segments",
            as: "audience_segments",
            type: "string",
          },
          { field: "creative_theme", as: "creative_theme", type: "string" },
          { field: "timeline", as: "prep_timeline", type: "string" },
          { field: "success_metrics", as: "kpi_targets", type: "string" },
        ],
      ),

      // 4. Audience list builder (HubSpot)
      hubspotPostNode(
        "4",
        "Build sale audience lists in HubSpot",
        760,
        "/crm/v3/lists",
        [
          { key: "name", value: "Seasonal Sale - {audience_segments}" },
          { key: "objectTypeId", value: "0-1" },
          { key: "processingType", value: "DYNAMIC" },
          {
            key: "filterBranch",
            value:
              '{"filterBranchType":"OR","filters":[{"filterType":"PROPERTY","property":"lifecyclestage","operation":{"operator":"IS_ANY_OF","values":["customer","lead","marketingqualifiedlead"]}}]}',
          },
        ],
        [
          { name: "listId", customName: "hubspot_list_id", type: "string" },
          { name: "name", customName: "list_name", type: "string" },
        ],
      ),

      // 5. Creative brief generator
      aiNode(
        "5",
        "AI: Generate creative briefs for sale assets",
        1000,
        "You create detailed creative briefs for seasonal marketing campaigns. Return JSON.",
        "Strategy: {strategy_summary}. Theme: {creative_theme}. Channels: {channel_plan}. Segments: {audience_segments}. Company: {company_name}. Return email_brief (creative direction for email templates), social_brief (social media asset requirements), sms_brief (SMS messaging guidelines), banner_brief (website banner specifications), asset_list (complete list of assets needed with sizes).",
        [
          { field: "email_brief", as: "email_creative_brief", type: "string" },
          {
            field: "social_brief",
            as: "social_creative_brief",
            type: "string",
          },
          { field: "sms_brief", as: "sms_creative_brief", type: "string" },
          {
            field: "banner_brief",
            as: "banner_creative_brief",
            type: "string",
          },
          { field: "asset_list", as: "full_asset_list", type: "string" },
        ],
      ),

      // 6. Asset checklist creator (Asana)
      asanaNode(
        "6",
        "Create asset checklist in Asana",
        1240,
        "Seasonal Sale Prep: {creative_theme}",
        "Strategy: {strategy_summary}\\n\\nTimeline: {prep_timeline}\\n\\nAssets Needed:\\n{full_asset_list}\\n\\nEmail Brief: {email_creative_brief}\\nSocial Brief: {social_creative_brief}\\nSMS Brief: {sms_creative_brief}\\nBanner Brief: {banner_creative_brief}\\n\\nKPIs: {kpi_targets}",
      ),

      // 7. Inventory check (Shopify)
      shopifyNode(
        "7",
        "Check inventory levels in Shopify",
        1480,
        "GET",
        "inventory_levels.json?location_ids={shopify_location_id}&limit=50",
        [],
        [
          {
            name: "inventory_levels[0].available",
            customName: "inventory_available_1",
            type: "number",
          },
          {
            name: "inventory_levels[0].inventory_item_id",
            customName: "inventory_item_1",
            type: "string",
          },
          {
            name: "inventory_levels",
            customName: "all_inventory_raw",
            type: "string",
          },
        ],
      ),

      // 8. Tech readiness validator (runCode)
      n("8", "runCode", "Validate technical readiness for sale", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate all systems are ready for the seasonal sale
const hubspotList = vars.hubspot_list_id || "";
const asanaTask = vars.asana_task_id || "";
const inventoryRaw = vars.all_inventory_raw || "[]";
const strategy = vars.strategy_summary || "";
const checks = [];
const ready = [];
if (hubspotList) ready.push("HubSpot list created: " + hubspotList);
else checks.push("HubSpot audience list not created");
if (asanaTask) ready.push("Asana task created: " + asanaTask);
else checks.push("Asana checklist not created");
let inventoryOk = true;
try {
  const inv = JSON.parse(inventoryRaw);
  const lowStock = inv.filter(i => (i.available || 0) < 10);
  if (lowStock.length > 0) {
    checks.push(lowStock.length + " products have low inventory");
    inventoryOk = false;
  } else {
    ready.push("Inventory levels adequate");
  }
} catch(e) {
  checks.push("Could not parse inventory data");
}
const readinessScore = Math.round((ready.length / (ready.length + checks.length)) * 100);
return {
  readiness_score: readinessScore,
  ready_items: ready.join("; "),
  issues_found: checks.length > 0 ? checks.join("; ") : "None",
  is_ready: checks.length === 0 ? "yes" : "needs_attention",
  inventory_status: inventoryOk ? "adequate" : "low_stock_alert",
  prep_completion_date: new Date().toISOString()
};`,
          outputVariables: [
            { name: "readiness_score", type: "number" },
            { name: "ready_items", type: "string" },
            { name: "issues_found", type: "string" },
            { name: "is_ready", type: "string" },
            { name: "inventory_status", type: "string" },
            { name: "prep_completion_date", type: "string" },
          ],
        },
      }),

      // 9. Prep completion reporter (Slack)
      slackNode(
        "9",
        "Report prep completion to Slack",
        1960,
        "Seasonal Sale Prep Complete\n\n*Strategy:* {strategy_summary}\n*Theme:* {creative_theme}\n*Channels:* {channel_plan}\n\n*Readiness Score:* {readiness_score}%\n*Status:* {is_ready}\n*Inventory:* {inventory_status}\n\n*Ready:* {ready_items}\n*Issues:* {issues_found}\n\n*HubSpot List:* {list_name}\n*Asana Tasks:* {asana_task_url}\n*KPIs:* {kpi_targets}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1187. SENDGRID DYNAMIC TEMPLATE SEND (7 nodes)
  // ===================================================================

  "SendGrid Dynamic Template Send": {
    summary:
      "Sends transactional or marketing emails via SendGrid dynamic templates. Triggers on a send request, validates recipients via runCode, maps dynamic data to template variables, dispatches via SendGrid batch API, processes event webhooks for delivery tracking, handles bounces and complaints via runCode, and logs delivery analytics to Google Sheets.",
    nodes: [
      trigger("Webhook: SendGrid template send request", "webhook_app", {
        source: "application",
        event: "email.send_requested",
        howItStarts:
          "Fires when your application requests a dynamic template email send, including recipient details, template ID, and dynamic data for personalization",
        setup:
          "Configure your application to POST email send requests to this workflow URL with recipient info, template ID, and dynamic data. Set SendGrid API key with mail send permissions. Set Google Sheets spreadsheet ID for delivery analytics.",
        inputFields: [],
      }),

      // 2. Recipient validator (runCode)
      n("2", "runCode", "Validate recipients and parse template data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse send request and validate recipient data
const payload = JSON.parse(vars.webhook_payload || "{}");
const to = (payload.to || payload.recipient_email || payload.email || "").toLowerCase().trim();
const recipientName = payload.recipient_name || payload.name || "";
const templateId = payload.template_id || payload.sendgrid_template || "";
const dynamicData = payload.dynamic_data || payload.template_data || payload.personalizations || {};
const subject = payload.subject || "";
const isValid = to.includes("@") && templateId.length > 0;
const batchId = "sg_batch_" + Date.now();
return {
  recipient_email: to,
  recipient_name: recipientName,
  template_id: templateId,
  dynamic_template_data: JSON.stringify(dynamicData),
  custom_subject: subject,
  is_valid_request: isValid ? "yes" : "no",
  batch_id: batchId,
  requested_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "recipient_email", type: "string" },
            { name: "recipient_name", type: "string" },
            { name: "template_id", type: "string" },
            { name: "dynamic_template_data", type: "string" },
            { name: "custom_subject", type: "string" },
            { name: "is_valid_request", type: "string" },
            { name: "batch_id", type: "string" },
            { name: "requested_at", type: "string" },
          ],
        },
      }),

      // 3. Dynamic data mapper (condition — valid request?)
      n("3", "condition", "Is the send request valid?", 520, {
        conditionDetails: {
          conditions: [
            {
              variable: "{is_valid_request}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 4. SendGrid batch sender
      n("4", "hitApi", "Send email via SendGrid dynamic template", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value:
                '[{"to":[{"email":"{recipient_email}","name":"{recipient_name}"}],"dynamic_template_data":{dynamic_template_data}}]',
            },
            {
              key: "from",
              value: '{"email":"{from_email}","name":"{company_name}"}',
            },
            { key: "template_id", value: "{template_id}" },
            { key: "batch_id", value: "{batch_id}" },
            {
              key: "tracking_settings",
              value:
                '{"click_tracking":{"enable":true},"open_tracking":{"enable":true}}',
            },
          ],
          variables: [
            {
              name: "statusCode",
              customName: "sg_send_status",
              type: "number",
            },
          ],
        },
      }),

      // 5. Event webhook processor (delay for events)
      n("5", "delayAction", "Wait for SendGrid delivery events", 1000, {
        delayDetails: { duration: 2, unit: "hours" },
      }),

      // 6. Bounce + complaint handler (runCode)
      n("6", "runCode", "Process delivery events and handle bounces", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process SendGrid event data and classify delivery outcomes
const email = vars.recipient_email || "";
const batchId = vars.batch_id || "";
const templateId = vars.template_id || "";
const sendStatus = parseInt(vars.sg_send_status || "0", 10);
const sentAt = vars.requested_at || "";
const deliveredSuccessfully = sendStatus === 202 || sendStatus === 200;
const processedAt = new Date().toISOString();
let deliveryOutcome = "unknown";
if (deliveredSuccessfully) deliveryOutcome = "delivered";
else if (sendStatus === 400) deliveryOutcome = "invalid_request";
else if (sendStatus === 401) deliveryOutcome = "auth_error";
else if (sendStatus === 429) deliveryOutcome = "rate_limited";
else if (sendStatus >= 500) deliveryOutcome = "server_error";
return {
  delivery_outcome: deliveryOutcome,
  is_delivered: deliveredSuccessfully ? "yes" : "no",
  delivery_email: email,
  delivery_batch_id: batchId,
  delivery_template: templateId,
  sent_at: sentAt,
  processed_at: processedAt,
  needs_retry: (!deliveredSuccessfully && sendStatus !== 400) ? "yes" : "no",
  sg_status_code: sendStatus
};`,
          outputVariables: [
            { name: "delivery_outcome", type: "string" },
            { name: "is_delivered", type: "string" },
            { name: "delivery_email", type: "string" },
            { name: "delivery_batch_id", type: "string" },
            { name: "delivery_template", type: "string" },
            { name: "sent_at", type: "string" },
            { name: "processed_at", type: "string" },
            { name: "needs_retry", type: "string" },
            { name: "sg_status_code", type: "number" },
          ],
        },
      }),

      // 7. Delivery analytics logger (Sheets)
      sheetsNode(
        "7",
        "Log delivery analytics to Sheets",
        1480,
        "SendGridAnalytics",
        '[["{sent_at}","{delivery_email}","{delivery_batch_id}","{delivery_template}","{delivery_outcome}","{sg_status_code}","{is_delivered}","{needs_retry}","{processed_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
